import { useState, useEffect, useMemo } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { supabase } from "../supabaseClient"
import {
  listTemplates,
  loadTemplate,
  saveTemplate,
  deleteTemplate,
  defaultTemplate,
} from "../templates/ga4Template"
import { useOrganization } from "../OrganizationContext"
import { useUserRole } from "../UserRoleContext"
import { useUser } from "../UserContext"
import {
  CHANNEL_GROUP_DIMENSION_SUGGESTIONS,
  getChannelGroupingValueSuggestions,
} from "../constants/channelGroupingSuggestions"
import { GA4_STANDARD_DIMENSION_SUGGESTIONS } from "../constants/ga4Dimensions"
import { Button, Input, Checkbox } from "../components/ui"
import {
  PROPERTY_TYPE_LABELS,
  DEFAULT_PROPERTY_TYPE,
  getDefaultTemplateContextKey,
  loadDefaultTemplateMap,
  setDefaultTemplateForType,
  subscribeToDefaultTemplateChanges,
  ensureDefaultTemplateMap,
  refreshDefaultTemplateMap,
} from "../utils/ga4TemplateDefaults"
import "./GA4Template.css"
import {
  DEFAULT_ENHANCED_MEASUREMENT_SETTINGS,
  normalizeEnhancedMeasurementSettings,
} from "../utils/ga4EnhancedMeasurement"

const MATCH_TYPE_OPTIONS = [
  "MATCH_TYPE_UNSPECIFIED",
  "EXACT",
  "BEGINS_WITH",
  "ENDS_WITH",
  "CONTAINS",
  "FULL_REGEXP",
  "PARTIAL_REGEXP",
]

const MATCHING_OPERATOR_OPTIONS = [
  { value: "EXACT|true", label: "equals" },
  { value: "EXACT|false", label: "equals (ignore case)" },
  { value: "BEGINS_WITH|true", label: "begins with" },
  { value: "BEGINS_WITH|false", label: "begins with (ignore case)" },
  { value: "ENDS_WITH|true", label: "ends with" },
  { value: "ENDS_WITH|false", label: "ends with (ignore case)" },
  { value: "CONTAINS|true", label: "contains" },
  { value: "CONTAINS|false", label: "contains (ignore case)" },
  { value: "FULL_REGEXP|true", label: "matches regex (full)" },
  { value: "PARTIAL_REGEXP|true", label: "matches regex (partial)" },
]

const PARAMETER_MUTATION_OPERATIONS = [
  { value: "SET_VALUE", label: "Set value" },
  { value: "COPY_PARAMETER", label: "Copy parameter" },
]

const PARAMETER_VALUE_TYPE_OPTIONS = [
  { value: "STRING", label: "Text" },
  { value: "INT", label: "Integer" },
  { value: "DOUBLE", label: "Number" },
  { value: "BOOLEAN", label: "Boolean" },
]

const BOOLEAN_VALUE_OPTIONS = [
  { value: "true", label: "True" },
  { value: "false", label: "False" },
]

const VALID_MATCH_TYPES = new Set(MATCH_TYPE_OPTIONS)
const VALID_PARAMETER_OPERATIONS = new Set(
  PARAMETER_MUTATION_OPERATIONS.map((opt) => opt.value)
)
const VALID_PARAMETER_VALUE_TYPES = new Set(
  PARAMETER_VALUE_TYPE_OPTIONS.map((opt) => opt.value)
)

const createDefaultMatchingCondition = () => ({
  eventParameter: "",
  matchType: "MATCH_TYPE_UNSPECIFIED",
  value: "",
  caseSensitive: false,
})

const createDefaultParameterMutation = () => ({
  parameter: "",
  operation: "SET_VALUE",
  valueType: "STRING",
  value: "",
})

const createInitialMatchingCondition = (isFirst = false) => {
  const condition = createDefaultMatchingCondition()
  if (isFirst) {
    condition.eventParameter = "event_name"
    condition.matchType = "EXACT"
    condition.caseSensitive = true
  }
  return condition
}

const toNormalizedBoolean = (value) => {
  if (typeof value === "boolean") return value
  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase()
    if (trimmed === "true") return true
    if (trimmed === "false") return false
  }
  return null
}

const normalizeTemplateMatchingCondition = (condition) => {
  if (!condition || typeof condition !== "object") return null
  const eventParameter =
    typeof condition.eventParameter === "string"
      ? condition.eventParameter.trim()
      : typeof condition.parameter === "string"
      ? condition.parameter.trim()
      : ""
  const rawMatchType =
    typeof condition.matchType === "string"
      ? condition.matchType.trim().toUpperCase()
      : typeof condition.operator === "string"
      ? condition.operator.trim().toUpperCase()
      : "MATCH_TYPE_UNSPECIFIED"
  const matchType = VALID_MATCH_TYPES.has(rawMatchType)
    ? rawMatchType
    : "MATCH_TYPE_UNSPECIFIED"
  const value =
    typeof condition.value === "string"
      ? condition.value
      : typeof condition.comparisonValue === "string"
      ? condition.comparisonValue
      : ""
  const caseSensitive =
    typeof condition.caseSensitive === "boolean"
      ? condition.caseSensitive
      : typeof condition.ignoreCase === "boolean"
      ? !condition.ignoreCase
      : undefined
  const negated =
    typeof condition.negated === "boolean" ? condition.negated : undefined
  if (!eventParameter && !value && matchType === "MATCH_TYPE_UNSPECIFIED") {
    return null
  }
  const normalized = { eventParameter, matchType, value }
  if (caseSensitive !== undefined) normalized.caseSensitive = caseSensitive
  if (negated !== undefined) normalized.negated = negated
  return normalized
}

const normalizeTemplateParameterMutation = (mutation) => {
  if (!mutation || typeof mutation !== "object") return null
  const parameter =
    typeof mutation.parameter === "string"
      ? mutation.parameter.trim()
      : ""
  if (!parameter) return null
  let operation =
    typeof mutation.operation === "string"
      ? mutation.operation.trim().toUpperCase()
      : ""
  const copyFromParameter =
    typeof mutation.copyFromParameter === "string"
      ? mutation.copyFromParameter.trim()
      : ""
  if (!VALID_PARAMETER_OPERATIONS.has(operation)) {
    operation = copyFromParameter ? "COPY_PARAMETER" : "SET_VALUE"
  }

  const normalized = { parameter, operation }

  if (operation === "COPY_PARAMETER") {
    if (copyFromParameter) normalized.copyFromParameter = copyFromParameter
    return normalized
  }

  let valueType =
    typeof mutation.valueType === "string"
      ? mutation.valueType.trim().toUpperCase()
      : ""
  let value = mutation.value

  const parameterValue =
    mutation.parameterValue ||
    (mutation.parameterValueExpression &&
    typeof mutation.parameterValueExpression === "object"
      ? mutation.parameterValueExpression.parameterValue
      : null)
  if (parameterValue && typeof parameterValue === "object") {
    if (typeof parameterValue.stringValue === "string") {
      valueType = "STRING"
      value = parameterValue.stringValue
    } else if (
      typeof parameterValue.doubleValue === "number" ||
      typeof parameterValue.doubleValue === "string"
    ) {
      valueType = "DOUBLE"
      value = String(parameterValue.doubleValue)
    } else if (
      typeof parameterValue.intValue === "number" ||
      typeof parameterValue.intValue === "string"
    ) {
      valueType = "INT"
      value = String(parameterValue.intValue)
    } else if (typeof parameterValue.boolValue === "boolean") {
      valueType = "BOOLEAN"
      value = parameterValue.boolValue ? "true" : "false"
    }
  }

  if (valueType && !VALID_PARAMETER_VALUE_TYPES.has(valueType)) {
    valueType = "STRING"
  }
  if (!valueType) valueType = "STRING"

  if (valueType === "BOOLEAN") {
    const boolValue = toNormalizedBoolean(value)
    normalized.value = boolValue === null ? "" : boolValue ? "true" : "false"
  } else if (value == null) {
    normalized.value = ""
  } else if (typeof value === "string") {
    normalized.value = value
  } else {
    normalized.value = String(value)
  }
  normalized.valueType = valueType
  return normalized
}

const formatEventParameterSuggestionLabel = (option) => {
  if (!option || typeof option !== "object") return undefined
  const descriptorParts = []
  if (
    typeof option.displayName === "string" &&
    option.displayName.trim().length
  ) {
    descriptorParts.push(option.displayName.trim())
  }
  if (
    typeof option.scope === "string" &&
    option.scope.trim().length &&
    option.scope.trim().toUpperCase() !== "EVENT"
  ) {
    descriptorParts.push(`${option.scope.trim()} scope`)
  }
  if (option.source === "custom") {
    descriptorParts.push("Custom dimension")
  } else if (option.source === "standard") {
    descriptorParts.push("Standard dimension")
  }
  if (!descriptorParts.length) return option.parameterName || undefined
  return `${option.parameterName} — ${descriptorParts.join(" • ")}`
}

const createDefaultChannelGroupFilter = () => ({
  fieldName: "",
  stringFilter: { matchType: "EXACT", value: "" },
})

const createDefaultFilterExpression = (negate = false) => {
  const filter = createDefaultChannelGroupFilter()
  return negate ? { notExpression: { filter } } : { filter }
}

const createDefaultOrGroupExpression = () => ({
  orGroup: {
    filterExpressions: [createDefaultFilterExpression()],
  },
})

const createDefaultChannelGroupExpression = () => ({
  andGroup: {
    filterExpressions: [createDefaultOrGroupExpression()],
  },
})

const createDefaultGroupingRule = () => ({
  displayName: "",
  expression: createDefaultChannelGroupExpression(),
})

const createDefaultChannelGroup = () => ({
  displayName: "",
  description: "",
  primary: false,
  groupingRules: [createDefaultGroupingRule()],
})

const cloneTemplateSettings = (template = defaultTemplate) =>
  JSON.parse(JSON.stringify(template))

const getDefaultEnhancedMeasurementSettings = () => ({
  ...DEFAULT_ENHANCED_MEASUREMENT_SETTINGS,
})

const normalizeChannelGroupFilter = (filter) => {
  const fieldName =
    filter && typeof filter.fieldName === "string" ? filter.fieldName : ""
  const stringFilter =
    filter && typeof filter.stringFilter === "object"
      ? filter.stringFilter
      : null
  const inListFilter =
    filter && typeof filter.inListFilter === "object"
      ? filter.inListFilter
      : null

  if (inListFilter) {
    const values = Array.isArray(inListFilter.values)
      ? inListFilter.values.filter((v) => typeof v === "string")
      : []
    return { fieldName, inListFilter: { values } }
  }

  const matchType =
    stringFilter && typeof stringFilter.matchType === "string"
      ? stringFilter.matchType
      : "EXACT"
  const normalizedMatchType = VALID_MATCH_TYPES.has(matchType)
    ? matchType
    : "EXACT"
  const value =
    stringFilter && typeof stringFilter.value === "string"
      ? stringFilter.value
      : ""
  return {
    fieldName,
    stringFilter: { matchType: normalizedMatchType, value },
  }
}

const normalizeFilterClause = (clause) => {
  if (clause && typeof clause === "object") {
    if (clause.filter) {
      return { filter: normalizeChannelGroupFilter(clause.filter) }
    }
    if (clause.notExpression?.filter) {
      return {
        notExpression: {
          filter: normalizeChannelGroupFilter(clause.notExpression.filter),
        },
      }
    }
  }
  return createDefaultFilterExpression()
}

const normalizeOrGroupExpression = (expr) => {
  if (expr && typeof expr === "object") {
    if (expr.orGroup?.filterExpressions) {
      const filters = Array.isArray(expr.orGroup.filterExpressions)
        ? expr.orGroup.filterExpressions.map((item) =>
            normalizeFilterClause(item)
          )
        : []
      return {
        orGroup: {
          filterExpressions: filters.length
            ? filters
            : [createDefaultFilterExpression()],
        },
      }
    }
    if (expr.filter || expr.notExpression) {
      return {
        orGroup: { filterExpressions: [normalizeFilterClause(expr)] },
      }
    }
  }
  return createDefaultOrGroupExpression()
}

const normalizeChannelGroupExpression = (expr) => {
  if (expr && typeof expr === "object") {
    if (expr.andGroup?.filterExpressions) {
      const groups = Array.isArray(expr.andGroup.filterExpressions)
        ? expr.andGroup.filterExpressions.map((item) =>
            normalizeOrGroupExpression(item)
          )
        : []
      return {
        andGroup: {
          filterExpressions: groups.length
            ? groups
            : [createDefaultOrGroupExpression()],
        },
      }
    }
    if (expr.orGroup || expr.filter || expr.notExpression) {
      return {
        andGroup: {
          filterExpressions: [normalizeOrGroupExpression(expr)],
        },
      }
    }
  }
  return createDefaultChannelGroupExpression()
}

const normalizeGroupingRule = (rule) => {
  const displayName =
    rule && typeof rule.displayName === "string" ? rule.displayName : ""
  const expression = normalizeChannelGroupExpression(rule?.expression)
  return { displayName, expression }
}

const normalizeChannelGroup = (group) => {
  const displayName =
    group && typeof group.displayName === "string" ? group.displayName : ""
  const description =
    group && typeof group.description === "string" ? group.description : ""
  const primary = Boolean(group?.primary)
  const rules = Array.isArray(group?.groupingRules)
    ? group.groupingRules
        .filter((rule) => rule && typeof rule === "object")
        .map((rule) => normalizeGroupingRule(rule))
    : []
  if (!rules.length) rules.push(createDefaultGroupingRule())
  return {
    displayName,
    description,
    primary,
    groupingRules: rules,
  }
}

const normalizeChannelGroupings = (value) => {
  if (!Array.isArray(value)) return []
  return value
    .filter((group) => group && typeof group === "object")
    .map((group) => normalizeChannelGroup(group))
}

const normalizeTemplateRuleBoolean = (value) => {
  if (typeof value === "boolean") return value
  if (typeof value === "string") {
    const trimmed = value.trim().toLowerCase()
    if (trimmed === "true") return true
    if (trimmed === "false") return false
  }
  return undefined
}

const deriveSourceEventFromConditions = (conditions) => {
  if (!Array.isArray(conditions)) return ""
  for (const condition of conditions) {
    if (!condition || typeof condition !== "object") continue
    const parameter =
      typeof condition.eventParameter === "string"
        ? condition.eventParameter.trim()
        : ""
    if (parameter !== "event_name") continue
    const matchType =
      typeof condition.matchType === "string"
        ? condition.matchType.trim().toUpperCase()
        : ""
    if (matchType !== "EXACT") continue
    const value =
      typeof condition.value === "string"
        ? condition.value.trim()
        : ""
    if (!value) continue
    return value
  }
  return ""
}

const normalizeTemplateEventCreateRule = (rule) => {
  if (!rule || typeof rule !== "object") rule = {}
  const sourceEvent =
    typeof rule.sourceEvent === "string" ? rule.sourceEvent.trim() : ""
  const destinationEvent =
    typeof rule.destinationEvent === "string"
      ? rule.destinationEvent.trim()
      : ""
  const description =
    typeof rule.description === "string" ? rule.description.trim() : ""
  const disabled = normalizeTemplateRuleBoolean(rule.disabled)
  const matchingConditions = Array.isArray(rule.matchingConditions)
    ? rule.matchingConditions
        .map((condition) => normalizeTemplateMatchingCondition(condition))
        .filter(Boolean)
    : []
  if (!matchingConditions.length && sourceEvent) {
    matchingConditions.push({
      eventParameter: "event_name",
      matchType: "EXACT",
      value: sourceEvent,
      caseSensitive: true,
    })
  }
  const derivedSourceEvent = deriveSourceEventFromConditions(matchingConditions)
  const parameterMutations = Array.isArray(rule.parameterMutations)
    ? rule.parameterMutations
        .map((mutation) => normalizeTemplateParameterMutation(mutation))
        .filter(Boolean)
    : []
  const copyParameters = normalizeTemplateRuleBoolean(rule.copyParameters)

  const normalized = {}
  if (sourceEvent || derivedSourceEvent) {
    normalized.sourceEvent = sourceEvent || derivedSourceEvent
  }
  normalized.destinationEvent = destinationEvent
  if (matchingConditions.length) normalized.matchingConditions = matchingConditions
  if (parameterMutations.length) normalized.parameterMutations = parameterMutations
  if (description) normalized.description = description
  if (copyParameters === true) normalized.copyParameters = true
  if (disabled !== undefined) normalized.disabled = disabled
  return normalized
}

const normalizeTemplateEventEditRule = (rule) => {
  if (!rule || typeof rule !== "object") rule = {}
  const eventName =
    typeof rule.eventName === "string" ? rule.eventName.trim() : ""
  const description =
    typeof rule.description === "string" ? rule.description.trim() : ""
  const disabled = normalizeTemplateRuleBoolean(rule.disabled)
  const matchingConditions = Array.isArray(rule.matchingConditions)
    ? rule.matchingConditions
        .map((condition) => normalizeTemplateMatchingCondition(condition))
        .filter(Boolean)
    : []
  const parameterMutations = Array.isArray(rule.parameterMutations)
    ? rule.parameterMutations
        .map((mutation) => normalizeTemplateParameterMutation(mutation))
        .filter(Boolean)
    : []

  const normalized = {}
  normalized.eventName = eventName
  if (matchingConditions.length) normalized.matchingConditions = matchingConditions
  if (parameterMutations.length) normalized.parameterMutations = parameterMutations
  if (description) normalized.description = description
  if (disabled !== undefined) normalized.disabled = disabled
  return normalized
}

const normalizeTemplateBigQueryLink = (link) => {
  if (!link || typeof link !== "object") return null
  const project =
    typeof link.project === "string" ? link.project.trim() : ""
  if (!project) return null
  const normalized = { project }
  if (link.dailyExportEnabled === true) normalized.dailyExportEnabled = true
  else if (link.dailyExportEnabled === false)
    normalized.dailyExportEnabled = false
  if (link.streamingExportEnabled === true)
    normalized.streamingExportEnabled = true
  else if (link.streamingExportEnabled === false)
    normalized.streamingExportEnabled = false
  if (link.freshDailyExportEnabled === true)
    normalized.freshDailyExportEnabled = true
  else if (link.freshDailyExportEnabled === false)
    normalized.freshDailyExportEnabled = false
  if (link.includeAdvertisingId === true)
    normalized.includeAdvertisingId = true
  else if (link.includeAdvertisingId === false)
    normalized.includeAdvertisingId = false
  return normalized
}

export default function GA4Template() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { selectedOrgId } = useOrganization()
  const role = useUserRole()
  const user = useUser()

  const [templates, setTemplates] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [name, setName] = useState("")
  const [settings, setSettings] = useState(() => cloneTemplateSettings())
  const [propertyType, setPropertyType] = useState("PROPERTY_TYPE_ORDINARY")
  const [templateOrg, setTemplateOrg] = useState(null)
  const [error, setError] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [initialSnapshot, setInitialSnapshot] = useState(() =>
    JSON.stringify({
      name: "",
      templateOrg: null,
      settings: cloneTemplateSettings(),
    })
  )
  const [defaultTemplates, setDefaultTemplates] = useState(() =>
    ensureDefaultTemplateMap()
  )
  const [organizationDimensionSuggestions, setOrganizationDimensionSuggestions] =
    useState([])
  const [organizationMetricSuggestions, setOrganizationMetricSuggestions] =
    useState([])
  const [organizationKeyEventSuggestions, setOrganizationKeyEventSuggestions] =
    useState([])
  const [defaultTemplateSaving, setDefaultTemplateSaving] = useState(null)
  const standardEventParameterSuggestions = useMemo(() => {
    const map = new Map()
    for (const item of GA4_STANDARD_DIMENSION_SUGGESTIONS) {
      if (!item || typeof item !== "object") continue
      const rawParam =
        typeof item.parameterName === "string" ? item.parameterName.trim() : ""
      if (!rawParam) continue
      const key = rawParam.toLowerCase()
      if (map.has(key)) continue
      map.set(key, {
        parameterName: rawParam,
        displayName:
          typeof item.displayName === "string" ? item.displayName.trim() : "",
        description:
          typeof item.description === "string"
            ? item.description.trim()
            : "",
        scope:
          typeof item.scope === "string" && item.scope.trim().length
            ? item.scope.trim().toUpperCase()
            : "EVENT",
        source: "standard",
      })
    }
    return Array.from(map.values())
  }, [])
  const eventParameterSuggestions = useMemo(() => {
    const map = new Map()
    for (const option of standardEventParameterSuggestions) {
      const param =
        typeof option?.parameterName === "string"
          ? option.parameterName.trim()
          : ""
      if (!param) continue
      map.set(param.toLowerCase(), { ...option })
    }
    for (const suggestion of organizationDimensionSuggestions) {
      if (!suggestion || typeof suggestion !== "object") continue
      const param =
        typeof suggestion.parameterName === "string"
          ? suggestion.parameterName.trim()
          : ""
      if (!param) continue
      const key = param.toLowerCase()
      const existing = map.get(key) || {}
      map.set(key, {
        parameterName: param,
        displayName:
          typeof suggestion.displayName === "string" &&
          suggestion.displayName.trim().length
            ? suggestion.displayName.trim()
            : existing.displayName || "",
        description:
          typeof suggestion.description === "string" &&
          suggestion.description.trim().length
            ? suggestion.description.trim()
            : existing.description || "",
        scope:
          typeof suggestion.scope === "string" &&
          suggestion.scope.trim().length
            ? suggestion.scope.trim().toUpperCase()
            : existing.scope || "EVENT",
        source: "custom",
      })
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.parameterName || "").localeCompare(b.parameterName || "", undefined, {
        sensitivity: "base",
      })
    )
  }, [organizationDimensionSuggestions, standardEventParameterSuggestions])
  const stepTitles = [
    "Name Your Template",
    "What Kind Of Properties Is This For?",
    "General Settings",
    "Enhanced Measurement",
    "Custom Dimensions",
    "Key Events",
    "Custom Event Rules",
    "BigQuery Linking",
    "Channel Groupings",
    "User Access",
  ]
  const [currentStep, setCurrentStep] = useState(0)

  const timeZoneOptions =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : []
  const currencyOptions =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("currency")
      : []
  const countingMethodOptions = [
    "COUNTING_METHOD_UNSPECIFIED",
    "ONCE_PER_EVENT",
    "ONCE_PER_SESSION",
  ]
  const propertyTypeOptions = [
    "PROPERTY_TYPE_ORDINARY",
    "PROPERTY_TYPE_360",
  ]
  const bigQueryBooleanOptions = [
    { value: "ignore", label: "Any" },
    { value: "true", label: "Enabled" },
    { value: "false", label: "Disabled" },
  ]
  const toBooleanSelectValue = (value) =>
    value === true ? "true" : value === false ? "false" : "ignore"
  const fromBooleanSelectValue = (value) =>
    value === "true" ? true : value === "false" ? false : undefined
  const updateBigQueryLink = (index, updater) => {
    setSettings((prev) => {
      const links = Array.isArray(prev.bigQueryLinks)
        ? prev.bigQueryLinks.map((link) => ({ ...link }))
        : []
      if (index < 0 || index >= links.length) return prev
      const currentLink = links[index] || {}
      const updated = updater({ ...currentLink }, links)
      if (updated == null) {
        links.splice(index, 1)
      } else {
        links[index] = updated
      }
      return { ...prev, bigQueryLinks: links }
    })
  }
  const enhancedMeasurementFieldConfig = [
    { key: "streamEnabled", label: "Stream enabled" },
    { key: "scrollsEnabled", label: "Scrolls" },
    { key: "outboundClicksEnabled", label: "Outbound clicks" },
    { key: "siteSearchEnabled", label: "Site search" },
    { key: "videoEngagementEnabled", label: "Video engagement" },
    { key: "fileDownloadsEnabled", label: "File downloads" },
    { key: "pageChangesEnabled", label: "Page changes" },
    { key: "formInteractionsEnabled", label: "Form interactions" },
  ]

  const updateCreateRule = (index, updater) => {
    setSettings((prev) => {
      const rules = [...(prev.eventCreateRules || [])]
      if (index < 0 || index >= rules.length) return prev
      const currentRule = { ...rules[index] }
      const updatedRule = updater(currentRule, rules)
      if (updatedRule == null) {
        rules.splice(index, 1)
      } else {
        rules[index] = updatedRule
      }
      return { ...prev, eventCreateRules: rules }
    })
  }

  const updateEditRule = (index, updater) => {
    setSettings((prev) => {
      const rules = [...(prev.eventEditRules || [])]
      if (index < 0 || index >= rules.length) return prev
      const currentRule = { ...rules[index] }
      const updatedRule = updater(currentRule, rules)
      if (updatedRule == null) {
        rules.splice(index, 1)
      } else {
        rules[index] = updatedRule
      }
      return { ...prev, eventEditRules: rules }
    })
  }

  const addCreateRule = () => {
    setSettings((prev) => ({
      ...prev,
      eventCreateRules: [
        ...(prev.eventCreateRules || []),
        {
          destinationEvent: "",
          matchingConditions: [createInitialMatchingCondition(true)],
          parameterMutations: [],
        },
      ],
    }))
  }

  const addEditRule = () => {
    setSettings((prev) => ({
      ...prev,
      eventEditRules: [
        ...(prev.eventEditRules || []),
        {
          eventName: "",
          matchingConditions: [createInitialMatchingCondition(true)],
          parameterMutations: [],
        },
      ],
    }))
  }
  const contextKey = useMemo(
    () =>
      getDefaultTemplateContextKey({
        orgId: selectedOrgId ?? null,
        userId: user?.id ?? null,
      }),
    [selectedOrgId, user?.id]
  )
  const totalSteps = stepTitles.length
  const isFinalStep = currentStep === totalSteps - 1
  const baseFieldOptions = {
    googleSignalsState: [
      "GOOGLE_SIGNALS_STATE_UNSPECIFIED",
      "GOOGLE_SIGNALS_ENABLED",
      "GOOGLE_SIGNALS_DISABLED",
    ],
    timeZone: timeZoneOptions,
    currencyCode: currencyOptions,
    industryCategory: [
      "INDUSTRY_CATEGORY_UNSPECIFIED",
      "AUTOMOTIVE",
      "BUSINESS_AND_INDUSTRIAL_MARKETS",
      "FINANCE",
      "HEALTHCARE",
      "TECHNOLOGY",
      "TRAVEL",
      "OTHER",
      "ARTS_AND_ENTERTAINMENT",
      "BEAUTY_AND_FITNESS",
      "BOOKS_AND_LITERATURE",
      "FOOD_AND_DRINK",
      "GAMES",
      "HOBBIES_AND_LEISURE",
      "HOME_AND_GARDEN",
      "INTERNET_AND_TELECOM",
      "LAW_AND_GOVERNMENT",
      "NEWS",
      "ONLINE_COMMUNITIES",
      "PEOPLE_AND_SOCIETY",
      "PETS_AND_ANIMALS",
      "REAL_ESTATE",
      "REFERENCE",
      "SCIENCE",
      "SPORTS",
      "JOBS_AND_EDUCATION",
      "SHOPPING",
    ],
    reportingIdentity: [
      "IDENTITY_BLENDING_STRATEGY_UNSPECIFIED",
      "BLENDED",
      "OBSERVED",
      "DEVICE_BASED",
    ],
    userDataRetention: [
      "RETENTION_DURATION_UNSPECIFIED",
      "TWO_MONTHS",
      "FOURTEEN_MONTHS",
      "TWENTY_SIX_MONTHS",
      "THIRTY_EIGHT_MONTHS",
      "FIFTY_MONTHS",
    ],
    eventDataRetention: [
      "RETENTION_DURATION_UNSPECIFIED",
      "TWO_MONTHS",
      "FOURTEEN_MONTHS",
      "TWENTY_SIX_MONTHS",
      "THIRTY_EIGHT_MONTHS",
      "FIFTY_MONTHS",
    ],
    resetUserDataOnNewActivity: ["true", "false"],
  }

  const normalizeSettings = (obj) => {
    const determineBigQueryAction = () => {
      const rawAction =
        typeof obj.bigQueryLinksAction === "string"
          ? obj.bigQueryLinksAction.trim().toLowerCase()
          : ""
      if (rawAction === "manage") return "manage"
      if (rawAction === "dont-manage" || rawAction === "ignore")
        return "dont-manage"
      const legacyMode =
        typeof obj.bigQueryLinksMode === "string"
          ? obj.bigQueryLinksMode.trim().toLowerCase()
          : ""
      if (legacyMode === "include" || legacyMode === "match") return "manage"
      return "dont-manage"
    }

    const result = {
      propertyTypes: {
        PROPERTY_TYPE_ORDINARY: {},
        PROPERTY_TYPE_360: {},
      },
      customDefinitionsMode: obj.customDefinitionsMode || "include",
      customDefinitions: Array.isArray(obj.customDefinitions)
        ? obj.customDefinitions.map((def) => {
            if (!def || typeof def !== "object") return def
            const { disallowAdsPersonalization, ...rest } = def
            return rest
          })
        : [],
      keyEventsMode: obj.keyEventsMode || "include",
      keyEvents: Array.isArray(obj.keyEvents) ? obj.keyEvents : [],
      eventCreateRulesMode: obj.eventCreateRulesMode || "include",
      eventCreateRules: Array.isArray(obj.eventCreateRules)
        ? obj.eventCreateRules
            .map((rule) => normalizeTemplateEventCreateRule(rule))
            .filter((rule) =>
              rule && typeof rule.destinationEvent === "string"
                ? rule.destinationEvent.length > 0
                : false
            )
        : [],
      eventEditRulesMode: obj.eventEditRulesMode || "include",
      eventEditRules: Array.isArray(obj.eventEditRules)
        ? obj.eventEditRules
            .map((rule) => normalizeTemplateEventEditRule(rule))
            .filter((rule) =>
              rule && typeof rule.eventName === "string"
                ? rule.eventName.length > 0
                : false
            )
        : [],
      bigQueryLinksAction: determineBigQueryAction(),
      bigQueryLinks: Array.isArray(obj.bigQueryLinks)
        ? obj.bigQueryLinks
            .map((link) => normalizeTemplateBigQueryLink(link))
            .filter(Boolean)
        : [],
      channelGroupings: normalizeChannelGroupings(obj.channelGroupings),
    }
    const rawDefaultType =
      typeof obj?.meta?.defaultPropertyType === "string"
        ? obj.meta.defaultPropertyType
        : typeof obj?.defaultPropertyType === "string"
          ? obj.defaultPropertyType
          : DEFAULT_PROPERTY_TYPE
    const normalizedDefaultType =
      rawDefaultType === "PROPERTY_TYPE_360"
        ? "PROPERTY_TYPE_360"
        : DEFAULT_PROPERTY_TYPE
    result.meta = {
      defaultPropertyType: normalizedDefaultType,
    }
    if (obj.enhancedMeasurementSettings === null) {
      result.enhancedMeasurementSettings = null
    } else if (obj.enhancedMeasurementSettings === false) {
      result.enhancedMeasurementSettings = null
    } else if (
      obj.enhancedMeasurementSettings &&
      typeof obj.enhancedMeasurementSettings === "object"
    ) {
      result.enhancedMeasurementSettings =
        normalizeEnhancedMeasurementSettings(obj.enhancedMeasurementSettings, {
          includeDefaults: true,
        })
    } else {
      result.enhancedMeasurementSettings =
        getDefaultEnhancedMeasurementSettings()
    }
    const normalizeFields = (o = {}) =>
      Object.fromEntries(
        Object.entries(o).map(([k, v]) =>
          v && typeof v === "object" && "value" in v && "action" in v
            ? [k, v]
            : [k, { value: v, action: "manage" }]
        )
      )
    result.propertyTypes.PROPERTY_TYPE_ORDINARY = normalizeFields(
      obj.propertyTypes?.PROPERTY_TYPE_ORDINARY || obj.PROPERTY_TYPE_ORDINARY || {}
    )
    result.propertyTypes.PROPERTY_TYPE_360 = normalizeFields(
      obj.propertyTypes?.PROPERTY_TYPE_360 || obj.PROPERTY_TYPE_360 || {}
    )
    return result
  }

  const getFieldOptions = (key) => {
    if (key === "userDataRetention" || key === "eventDataRetention") {
      return propertyType === "PROPERTY_TYPE_360"
        ? baseFieldOptions[key]
        : baseFieldOptions[key].slice(0, 3)
    }
    return baseFieldOptions[key]
  }

  const updateChannelGroupings = (updater) => {
    setSettings((prev) => {
      const next = JSON.parse(JSON.stringify(prev))
      if (!Array.isArray(next.channelGroupings)) next.channelGroupings = []
      updater(next)
      return next
    })
  }

  const updateExpectedUsers = (updater) => {
    setSettings((prev) => {
      const next = JSON.parse(JSON.stringify(prev))
      if (!next.userAccess || typeof next.userAccess !== "object") {
        next.userAccess = { expectedUsers: [] }
      }
      if (!Array.isArray(next.userAccess.expectedUsers)) {
        next.userAccess.expectedUsers = []
      }
      updater(next)
      return next
    })
  }

  const updateFilterClause = (
    groupIndex,
    ruleIndex,
    andIndex,
    filterIndex,
    updater
  ) => {
    updateChannelGroupings((draft) => {
      const clause =
        draft.channelGroupings?.[groupIndex]?.groupingRules?.[ruleIndex]?.expression?.andGroup?.filterExpressions?.[andIndex]?.orGroup?.filterExpressions?.[filterIndex]
      if (!clause) return
      let isNegated = Boolean(clause.notExpression)
      let filter = clause.filter || clause.notExpression?.filter
      if (!filter) {
        filter = createDefaultChannelGroupFilter()
        if (isNegated) clause.notExpression = { filter }
        else clause.filter = filter
      }
      const setFilter = (newFilter) => {
        if (isNegated) clause.notExpression = { filter: newFilter }
        else clause.filter = newFilter
        filter = newFilter
      }
      const setNegated = (negated) => {
        if (negated && !isNegated) {
          clause.notExpression = { filter }
          delete clause.filter
          isNegated = true
        } else if (!negated && isNegated) {
          clause.filter = filter
          delete clause.notExpression
          isNegated = false
        }
      }
      updater({ clause, filter, isNegated, setFilter, setNegated })
    })
  }

  useEffect(() => {
    async function fetchTemplates() {
      const list = await listTemplates(selectedOrgId)
      setTemplates(list)
      const paramId = searchParams.get("templateId")
      if (paramId) setSelectedId(paramId)
      else if (list.length > 0) {
        const orgTemplate = list.find((t) => t.organization_id === selectedOrgId)
        setSelectedId((orgTemplate || list[0]).id)
      }
    }
    fetchTemplates()
  }, [selectedOrgId, searchParams])

  useEffect(() => {
    async function fetchTemplate() {
      if (!selectedId) {
        if (isCreating) {
          setName("")
          const defaultSettings = cloneTemplateSettings()
          setSettings(defaultSettings)
          const nextOrg = selectedOrgId ?? null
          setTemplateOrg(nextOrg)
          setInitialSnapshot(
            JSON.stringify({
              name: "",
              templateOrg: nextOrg,
              settings: defaultSettings,
            })
          )
          const defaultType =
            defaultSettings?.meta?.defaultPropertyType || DEFAULT_PROPERTY_TYPE
          setPropertyType(defaultType)
        }
        return
      }
      const tpl = await loadTemplate(selectedId || null)
      const tplName = tpl.name || ""
      const normalizedSettings = normalizeSettings(tpl.template || {})
      const orgId = tpl.organization_id ?? null
      setName(tplName)
      setSettings(normalizedSettings)
      setTemplateOrg(orgId)
      setInitialSnapshot(
        JSON.stringify({
          name: tplName,
          templateOrg: orgId,
          settings: normalizedSettings,
        })
      )
      const defaultType =
        normalizedSettings?.meta?.defaultPropertyType || DEFAULT_PROPERTY_TYPE
      setPropertyType(defaultType)
      setIsCreating(false)
    }
    fetchTemplate()
  }, [selectedId, selectedOrgId, isCreating])

  useEffect(() => {
    const localMap = ensureDefaultTemplateMap(loadDefaultTemplateMap(contextKey))
    setDefaultTemplates(localMap)
    let isActive = true
    const syncDefaults = async () => {
      try {
        const map = await refreshDefaultTemplateMap(contextKey)
        if (isActive && map) {
          setDefaultTemplates(ensureDefaultTemplateMap(map))
        }
      } catch (err) {
        console.warn("[GA4] Failed to synchronize default templates", err)
      }
    }
    syncDefaults()
    const unsubscribe = subscribeToDefaultTemplateChanges((detail) => {
      if (!detail || detail.contextKey !== contextKey) return
      if (!isActive) return
      setDefaultTemplates(ensureDefaultTemplateMap(detail.map))
    })
    return () => {
      isActive = false
      if (typeof unsubscribe === "function") unsubscribe()
    }
  }, [contextKey])

  useEffect(() => {
    let isActive = true
    const activeOrgId = templateOrg ?? selectedOrgId ?? null

    const resetSuggestions = () => {
      if (!isActive) return
      setOrganizationDimensionSuggestions([])
      setOrganizationMetricSuggestions([])
      setOrganizationKeyEventSuggestions([])
    }

    if (!activeOrgId) {
      resetSuggestions()
      return
    }

    const loadSuggestions = async () => {
      try {
        const { data: properties, error: propError } = await supabase
          .from("ga4_properties")
          .select("id")
          .eq("organization_id", activeOrgId)
        if (propError) throw propError

        const propertyIds = (properties || [])
          .map((row) => row?.id)
          .filter(Boolean)
        if (!propertyIds.length) {
          resetSuggestions()
          return
        }

        const { data: settingsRows, error: settingsError } = await supabase
          .from("ga4_property_settings")
          .select("ga4_property_id, settings, created_at")
          .in("ga4_property_id", propertyIds)
          .order("created_at", { ascending: false })
        if (settingsError) throw settingsError

        const latestSettings = []
        const seenSettings = new Set()
        for (const row of settingsRows || []) {
          const propId = row?.ga4_property_id
          if (!propId || seenSettings.has(propId)) continue
          seenSettings.add(propId)
          latestSettings.push(row?.settings || null)
        }

        const dimensionMap = new Map()
        const metricMap = new Map()

        for (const settings of latestSettings) {
          const customDimensions =
            settings?.customDimensions?.customDimensions || []
          for (const dimension of customDimensions) {
            if (!dimension || typeof dimension !== "object") continue
            const param =
              typeof dimension.parameterName === "string"
                ? dimension.parameterName.trim()
                : ""
            if (!param) continue
            const key = param.toLowerCase()
            const existing = dimensionMap.get(key) || {}
            dimensionMap.set(key, {
              parameterName: param,
              displayName:
                dimension.displayName?.trim() || existing.displayName || "",
              description:
                dimension.description?.trim() || existing.description || "",
              scope: dimension.scope || existing.scope || "EVENT",
            })
          }

          const customMetrics = settings?.customMetrics?.customMetrics || []
          for (const metric of customMetrics) {
            if (!metric || typeof metric !== "object") continue
            const param =
              typeof metric.parameterName === "string"
                ? metric.parameterName.trim()
                : ""
            if (!param) continue
            const key = param.toLowerCase()
            const existing = metricMap.get(key) || {}
            metricMap.set(key, {
              parameterName: param,
              displayName:
                metric.displayName?.trim() || existing.displayName || "",
              description:
                metric.description?.trim() || existing.description || "",
              measurementUnit:
                metric.measurementUnit ||
                existing.measurementUnit ||
                "MEASUREMENT_UNIT_UNSPECIFIED",
              restrictedMetricType:
                metric.restrictedMetricType ||
                existing.restrictedMetricType ||
                "RESTRICTED_METRIC_TYPE_UNSPECIFIED",
            })
          }
        }

        const { data: keyEventRows, error: keyError } = await supabase
          .from("ga4_property_key_events")
          .select("ga4_property_id, events, created_at")
          .in("ga4_property_id", propertyIds)
          .order("created_at", { ascending: false })
        if (keyError) throw keyError

        const latestEvents = []
        const seenEvents = new Set()
        for (const row of keyEventRows || []) {
          const propId = row?.ga4_property_id
          if (!propId || seenEvents.has(propId)) continue
          seenEvents.add(propId)
          latestEvents.push(row?.events || null)
        }

        const keyEventMap = new Map()
        for (const events of latestEvents) {
          const conversions = events?.conversionEvents || []
          for (const evt of conversions) {
            if (!evt || typeof evt !== "object") continue
            const name =
              typeof evt.eventName === "string" ? evt.eventName.trim() : ""
            if (!name) continue
            const key = name.toLowerCase()
            const existing = keyEventMap.get(key) || {}
            const defaultValue = { ...existing.defaultValue }
            if (evt.defaultValue && typeof evt.defaultValue === "object") {
              if (
                typeof evt.defaultValue.numericValue === "number" &&
                Number.isFinite(evt.defaultValue.numericValue)
              ) {
                defaultValue.numericValue = evt.defaultValue.numericValue
              }
              if (evt.defaultValue.currencyCode) {
                defaultValue.currencyCode = evt.defaultValue.currencyCode
              }
            }
            keyEventMap.set(key, {
              eventName: name,
              countingMethod:
                evt.countingMethod ||
                existing.countingMethod ||
                "COUNTING_METHOD_UNSPECIFIED",
              defaultValue,
            })
          }
        }

        if (!isActive) return

        const dimensionSuggestions = Array.from(dimensionMap.values()).sort(
          (a, b) =>
            (a.parameterName || "").localeCompare(b.parameterName || "", undefined, {
              sensitivity: "base",
            })
        )
        const metricSuggestions = Array.from(metricMap.values()).sort((a, b) =>
          (a.parameterName || "").localeCompare(b.parameterName || "", undefined, {
            sensitivity: "base",
          })
        )
        const keyEventSuggestions = Array.from(keyEventMap.values()).sort(
          (a, b) =>
            (a.eventName || "").localeCompare(b.eventName || "", undefined, {
              sensitivity: "base",
            })
        )

        setOrganizationDimensionSuggestions(dimensionSuggestions)
        setOrganizationMetricSuggestions(metricSuggestions)
        setOrganizationKeyEventSuggestions(keyEventSuggestions)
      } catch (err) {
        console.error("[GA4 Template] Failed to load organization data", err)
        resetSuggestions()
      }
    }

    loadSuggestions()

    return () => {
      isActive = false
    }
  }, [selectedOrgId, templateOrg])

  const canEdit =
    templateOrg != null
      ? selectedOrgId != null && selectedOrgId === templateOrg
      : role === "admin"

  const handleSave = async () => {
    try {
      const id = await saveTemplate({
        id: selectedId || null,
        name,
        template: settings,
        orgId: templateOrg,
      })
      setError(null)
      setIsModalOpen(false)
      setIsCreating(false)
      navigate(`/ga4-template?templateId=${id}`)
    } catch (e) {
      setError(e.message)
    }
  }

  const handleDelete = async () => {
    if (!selectedId) return
    if (!window.confirm("Delete this template?")) return
    try {
      await deleteTemplate(selectedId)
      const list = await listTemplates(selectedOrgId)
      setTemplates(list)
      setSelectedId(null)
      setIsCreating(false)
      setIsModalOpen(false)
      setError(null)
      navigate("/ga4-template")
    } catch (e) {
      setError(e.message)
    }
  }

  const startNew = () => {
    const defaultSettings = cloneTemplateSettings()
    const nextOrg = selectedOrgId ?? null
    setSelectedId(null)
    setName("")
    setSettings(defaultSettings)
    setTemplateOrg(nextOrg)
    const defaultType =
      defaultSettings?.meta?.defaultPropertyType || DEFAULT_PROPERTY_TYPE
    setPropertyType(defaultType)
    setInitialSnapshot(
      JSON.stringify({
        name: "",
        templateOrg: nextOrg,
        settings: defaultSettings,
      })
    )
    setIsCreating(true)
    setCurrentStep(0)
    setIsModalOpen(true)
  }

  const handleCancel = () => {
    if (initialSnapshot) {
      try {
        const parsed = JSON.parse(initialSnapshot)
        setName(parsed.name || "")
        setSettings(parsed.settings || cloneTemplateSettings())
        const nextOrg =
          parsed.templateOrg != null ? parsed.templateOrg : selectedOrgId ?? null
        setTemplateOrg(nextOrg)
        const restoredType =
          parsed.settings?.meta?.defaultPropertyType || DEFAULT_PROPERTY_TYPE
        setPropertyType(restoredType)
      } catch (err) {
        setName("")
        setSettings(cloneTemplateSettings())
        setTemplateOrg(selectedOrgId ?? null)
        setPropertyType(DEFAULT_PROPERTY_TYPE)
      }
    } else {
      setName("")
      setSettings(cloneTemplateSettings())
      setTemplateOrg(selectedOrgId ?? null)
      setPropertyType(DEFAULT_PROPERTY_TYPE)
    }
    setError(null)
    setIsCreating(false)
    setCurrentStep(0)
    setIsModalOpen(false)
  }

  const bigQueryLinks = Array.isArray(settings.bigQueryLinks)
    ? settings.bigQueryLinks
    : []
  const bigQueryAction =
    typeof settings.bigQueryLinksAction === "string"
      ? settings.bigQueryLinksAction
      : "dont-manage"
  const isManagingBigQuery = bigQueryAction === "manage"
  const channelGroupings = settings.channelGroupings || []
  const expectedUserAccess =
    (settings.userAccess && Array.isArray(settings.userAccess.expectedUsers))
      ? settings.userAccess.expectedUsers
      : []
  const canProceedToNext = currentStep === 0 ? Boolean(name.trim()) : true
  const shouldUseWideModal =
    currentStep >= 2 &&
    (currentStep !== totalSteps - 1 || channelGroupings.length > 0 || canEdit)
  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        name: name || "",
        templateOrg: templateOrg ?? null,
        settings,
      }),
    [name, templateOrg, settings]
  )
  const hasUnsavedChanges =
    Boolean(initialSnapshot) && currentSnapshot !== initialSnapshot

  const handleModalDismiss = () => {
    if (hasUnsavedChanges) {
      const shouldSave = window.confirm("Do you want to save your changes?")
      if (shouldSave) {
        if (!name.trim()) {
          setError("Please enter a template name before saving.")
          return
        }
        handleSave()
        return
      }
    }
    handleCancel()
  }

  const handleStepSelect = (index) => {
    if (index === currentStep) return
    if (index > currentStep && !name.trim()) {
      setError("Please enter a template name before continuing.")
      return
    }
    setError(null)
    setCurrentStep(() => Math.min(Math.max(index, 0), totalSteps - 1))
  }

  const handleNextStep = () => {
    if (!isFinalStep) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps - 1))
    }
  }

  const handlePreviousStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0))
  }

  return (
    <div className="page-container">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <h2>GA4 Templates</h2>
        <button
          onClick={startNew}
          disabled={selectedOrgId == null && role !== "admin"}
        >
          Add Template
        </button>
      </div>
      {templates.length > 0 ? (
        <div style={{ marginBottom: "1rem", overflowX: "auto" }}>
          <div
            style={{
              maxHeight: "60vh",
              overflowY: "auto",
              border: "1px solid var(--border-color, #e2e8f0)",
              borderRadius: "var(--border-radius, 8px)",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
            }}
          >
            <table className="ui-table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "720px",
              }}
            >
              <thead>
                <tr>
                  {["Name", "Visibility", "Property Type", "Default Template", ""].map((header, index) => (
                    <th
                      key={`${header}-${index}`}
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#1e3a8a",
                        color: "white",
                        zIndex: 2,
                        textAlign: index === 4 ? "right" : "left",
                        padding: "0.5rem 0.75rem",
                        borderBottom: "1px solid var(--border-color, #e2e8f0)",
                        whiteSpace: index === 4 ? "nowrap" : "normal",
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => {
                  const propertyTypeKey =
                    t.template?.meta?.defaultPropertyType || DEFAULT_PROPERTY_TYPE
                  const propertyTypeLabel =
                    PROPERTY_TYPE_LABELS[propertyTypeKey] || "Standard"
                  const isDefault = defaultTemplates[propertyTypeKey] === t.id
                  const isSelected = selectedId === t.id
                  const selectedRowColor = "rgba(30, 64, 175, 0.12)"
                  const defaultRowColor = "rgba(34, 197, 94, 0.14)"
                  const rowBackgroundColor = isSelected
                    ? selectedRowColor
                    : isDefault
                    ? defaultRowColor
                    : "transparent"
                  const sharedCellStyle = {
                    padding: "0.5rem 0.75rem",
                    borderBottom: "1px solid var(--border-color, #e2e8f0)",
                    backgroundColor: rowBackgroundColor,
                  }
                  const actionButtonStyle = {
                    backgroundColor: "#1e3a8a",
                    border: "1px solid #1e3a8a",
                    color: "#ffffff",
                    padding: "0.35rem 0.85rem",
                    borderRadius: "var(--border-radius, 6px)",
                    cursor: "pointer",
                    fontWeight: 600,
                    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.25)",
                    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
                  }

                  return (
                    <tr
                      key={t.id}
                      onClick={() => {
                        setSelectedId(t.id)
                        setIsCreating(false)
                        setCurrentStep(0)
                        setIsModalOpen(true)
                      }}
                      style={{
                        backgroundColor: rowBackgroundColor,
                        cursor: "pointer",
                      }}
                    >
                      <td style={{ ...sharedCellStyle, fontWeight: 600 }}>{t.name}</td>
                      <td style={sharedCellStyle}>
                        {t.organization_id != null ? "Private" : "Public"}
                      </td>
                      <td style={sharedCellStyle}>{propertyTypeLabel}</td>
                      <td style={sharedCellStyle}>
                        {isDefault ? (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0.25rem",
                              backgroundColor: "#22c55e",
                              color: "#052e16",
                              borderRadius: "9999px",
                              padding: "0.15rem 0.5rem",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.2)",
                            }}
                          >
                            Default
                          </span>
                        ) : (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation()
                              if (defaultTemplateSaving) return
                              setDefaultTemplateSaving(propertyTypeKey)
                              try {
                                const nextMap = await setDefaultTemplateForType(
                                  contextKey,
                                  propertyTypeKey,
                                  t.id,
                                )
                                setDefaultTemplates(nextMap)
                                setError(null)
                              } catch (err) {
                                console.error(
                                  "[GA4] Failed to set default template",
                                  err,
                                )
                                setError(
                                  "Failed to update the default template. Please try again.",
                                )
                              } finally {
                                setDefaultTemplateSaving(null)
                              }
                            }}
                            disabled={defaultTemplateSaving === propertyTypeKey}
                            style={{
                              ...actionButtonStyle,
                              padding: "0.35rem 0.75rem",
                              ...(defaultTemplateSaving === propertyTypeKey
                                ? { opacity: 0.7, cursor: "wait" }
                                : {}),
                            }}
                          >
                            {defaultTemplateSaving === propertyTypeKey
                              ? "Saving..."
                              : "Select Default"}
                          </button>
                        )}
                      </td>
                      <td
                        style={{
                          ...sharedCellStyle,
                          textAlign: "right",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedId(t.id)
                            setIsCreating(false)
                            setCurrentStep(0)
                            setIsModalOpen(true)
                          }}
                          style={actionButtonStyle}
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        !isModalOpen && (
          <div style={{ marginBottom: "1rem" }}>
            <p>No templates</p>
            <button
              onClick={startNew}
              disabled={selectedOrgId == null && role !== "admin"}
            >
              Add Template
            </button>
          </div>
        )
      )}
      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: "1rem",
            boxSizing: "border-box",
          }}
          onClick={handleModalDismiss}
        >
          <div
            style={{
              backgroundColor: "var(--background-color)",
              color: "var(--text-color)",
              padding: "1.5rem",
              borderRadius: "8px",
              width: shouldUseWideModal ? "min(80vw, 1200px)" : "min(600px, 90vw)",
              maxHeight: "80vh",
              overflowY: "auto",
              overflowX: "hidden",
              boxSizing: "border-box",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleModalDismiss}
              style={{
                position: "absolute",
                top: "0.5rem",
                right: "0.5rem",
              }}
            >
              X
            </button>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
              >
                <ol
                  style={{
                    listStyle: "none",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                    padding: 0,
                    margin: 0,
                  }}
                >
                  {stepTitles.map((title, index) => (
                    <li key={title}>
                      <button
                        type="button"
                        onClick={() => handleStepSelect(index)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          padding: "0.25rem 0.75rem",
                          borderRadius: "999px",
                          border: "1px solid var(--border-color, #4b5563)",
                          backgroundColor:
                            index === currentStep
                              ? "var(--primary-color, #2563eb)"
                              : "transparent",
                          color:
                            index === currentStep
                              ? "#fff"
                              : "var(--text-color)",
                          fontSize: "0.85rem",
                          fontWeight: index === currentStep ? 600 : 400,
                          cursor:
                            index === currentStep
                              ? "default"
                              : "pointer",
                        }}
                        aria-current={index === currentStep ? "step" : undefined}
                      >
                        <span>{index + 1}</span>
                        <span>{title}</span>
                      </button>
                    </li>
                  ))}
                </ol>
                <h2 style={{ margin: 0 }}>{stepTitles[currentStep]}</h2>
              </div>
              <div style={{ display: currentStep === 0 ? "block" : "none" }}>
                <div>
                  <label style={{ display: "block" }}>Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!canEdit}
                    style={{ width: "100%" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block" }}>Template Property Type</label>
                  <select
                    value={
                      settings?.meta?.defaultPropertyType || DEFAULT_PROPERTY_TYPE
                    }
                    onChange={(e) => {
                      const value = e.target.value
                      setSettings((prev) => ({
                        ...prev,
                        meta: {
                          ...(prev.meta || {}),
                          defaultPropertyType: value,
                        },
                      }))
                      setPropertyType(value)
                    }}
                    disabled={!canEdit}
                    style={{ width: "100%" }}
                  >
                    {Object.entries(PROPERTY_TYPE_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                {role === "admin" && (
                  <div>
                    <label>
                      <input
                        type="checkbox"
                        checked={templateOrg == null}
                        onChange={(e) =>
                          setTemplateOrg(
                            e.target.checked ? null : selectedOrgId ?? null
                          )
                        }
                        disabled={!canEdit}
                      />
                      Public Template
                    </label>
                  </div>
                )}
              </div>
              <div style={{ display: currentStep === 1 ? "block" : "none" }}>
                <label style={{ display: "block" }}>Property Type</label>
                <select
                  value={propertyType}
                  onChange={(e) => setPropertyType(e.target.value)}
                  disabled={!canEdit}
                  style={{ width: "100%" }}
                >
                  {propertyTypeOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: currentStep === 2 ? "block" : "none" }}>
                <h3 style={{ marginTop: "1rem" }}>General Settings</h3>
                {Object.entries(settings.propertyTypes[propertyType] || {}).map(
                  ([key, cfg]) => {
                    const options = getFieldOptions(key)
                    return (
                      <div key={key}>
                        <label style={{ display: "block" }}>{key}</label>
                        <div
                          style={{
                            display: "flex",
                            gap: "0.5rem",
                            flexWrap: "wrap",
                          }}
                        >
                          {options ? (
                            <select
                              value={cfg.value}
                              onChange={(e) =>
                                setSettings((prev) => ({
                                  ...prev,
                                  propertyTypes: {
                                    ...prev.propertyTypes,
                                    [propertyType]: {
                                      ...prev.propertyTypes[propertyType],
                                      [key]: {
                                        ...prev.propertyTypes[propertyType][key],
                                        value: e.target.value,
                                      },
                                    },
                                  },
                                }))
                              }
                              disabled={!canEdit}
                              style={{ flex: 1, minWidth: 0 }}
                            >
                              {options.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={cfg.value}
                              onChange={(e) =>
                                setSettings((prev) => ({
                                  ...prev,
                                  propertyTypes: {
                                    ...prev.propertyTypes,
                                    [propertyType]: {
                                      ...prev.propertyTypes[propertyType],
                                      [key]: {
                                        ...prev.propertyTypes[propertyType][key],
                                        value: e.target.value,
                                      },
                                    },
                                  },
                                }))
                              }
                              disabled={!canEdit}
                              style={{ flex: 1, minWidth: 0 }}
                            />
                          )}
                          <select
                            value={cfg.action}
                            onChange={(e) =>
                              setSettings((prev) => ({
                                ...prev,
                                propertyTypes: {
                                  ...prev.propertyTypes,
                                  [propertyType]: {
                                    ...prev.propertyTypes[propertyType],
                                    [key]: {
                                      ...prev.propertyTypes[propertyType][key],
                                      action: e.target.value,
                                    },
                                  },
                                },
                              }))
                            }
                            disabled={!canEdit}
                            style={{ minWidth: "110px" }}
                          >
                            <option value="manage">manage</option>
                            <option value="ignore">ignore</option>
                          </select>
                          {canEdit && (
                            <button
                              onClick={() => {
                                const { [key]: _, ...rest } =
                                  settings.propertyTypes[propertyType]
                                setSettings((prev) => ({
                                  ...prev,
                                  propertyTypes: {
                                    ...prev.propertyTypes,
                                    [propertyType]: rest,
                                  },
                                }))
                              }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  }
                )}
              </div>

              <div style={{ display: currentStep === 3 ? "block" : "none" }}>
                <h3 style={{ marginTop: "1rem" }}>Enhanced Measurement Settings</h3>
                <p style={{ margin: "0 0 0.75rem", color: "#475569" }}>
                  Applies to web data streams. Resource names are ignored when
                  comparing settings.
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <label style={{ fontWeight: 600 }}>
                    Enhanced Measurement Settings
                  </label>
                  <select
                    value={
                      settings.enhancedMeasurementSettings !== null &&
                      settings.enhancedMeasurementSettings !== undefined
                        ? "manage"
                        : "ignore"
                    }
                    onChange={(event) => {
                      if (!canEdit) return
                      const nextMode = event.target.value === "ignore"
                        ? "ignore"
                        : "manage"
                      setSettings((prev) => {
                        if (nextMode === "ignore") {
                          return {
                            ...prev,
                            enhancedMeasurementSettings: null,
                          }
                        }
                        const normalized =
                          normalizeEnhancedMeasurementSettings(
                            prev.enhancedMeasurementSettings,
                            { includeDefaults: true }
                          ) || getDefaultEnhancedMeasurementSettings()
                        return {
                          ...prev,
                          enhancedMeasurementSettings: normalized,
                        }
                      })
                    }}
                    disabled={!canEdit}
                    style={{ minWidth: "120px" }}
                  >
                    <option value="manage">Manage</option>
                    <option value="ignore">Ignore</option>
                  </select>
                </div>
                {settings.enhancedMeasurementSettings && (
                  <div
                    style={{
                      marginTop: "1rem",
                      display: "grid",
                      gap: "0.75rem",
                      maxWidth: "480px",
                    }}
                  >
                    {enhancedMeasurementFieldConfig.map(({ key, label }) => {
                      const value = Boolean(
                        settings.enhancedMeasurementSettings?.[key]
                      )
                      return (
                        <div
                          key={key}
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <label
                            htmlFor={`enhanced-${key}`}
                            style={{ flex: "0 0 220px", fontWeight: 500 }}
                          >
                            {label}
                          </label>
                          <select
                            id={`enhanced-${key}`}
                            value={value ? "true" : "false"}
                            onChange={(event) => {
                              const nextValue = event.target.value === "true"
                              setSettings((prev) => {
                                const normalized =
                                  normalizeEnhancedMeasurementSettings(
                                    prev.enhancedMeasurementSettings,
                                    { includeDefaults: true }
                                  ) ||
                                  getDefaultEnhancedMeasurementSettings()
                                return {
                                  ...prev,
                                  enhancedMeasurementSettings: {
                                    ...normalized,
                                    [key]: nextValue,
                                  },
                                }
                              })
                            }}
                            disabled={!canEdit}
                            style={{ minWidth: "120px" }}
                          >
                            <option value="true">true</option>
                            <option value="false">false</option>
                          </select>
                        </div>
                      )
                    })}
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <label htmlFor="enhanced-search-query">
                        Search query parameters
                      </label>
                      <input
                        id="enhanced-search-query"
                        type="text"
                        value={
                          settings.enhancedMeasurementSettings
                            ?.searchQueryParameter || ""
                        }
                        onChange={(event) => {
                          const formatted = event.target.value
                            .split(",")
                            .map((item) => item.trim())
                            .filter((item) => item.length > 0)
                            .join(",")
                          setSettings((prev) => {
                            const normalized =
                              normalizeEnhancedMeasurementSettings(
                                prev.enhancedMeasurementSettings,
                                { includeDefaults: true }
                              ) ||
                              getDefaultEnhancedMeasurementSettings()
                            return {
                              ...prev,
                              enhancedMeasurementSettings: {
                                ...normalized,
                                searchQueryParameter: formatted,
                              },
                            }
                          })
                        }}
                        disabled={!canEdit}
                      />
                      <small style={{ color: "#475569" }}>
                        Separate multiple parameters with commas.
                      </small>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: currentStep === 4 ? "block" : "none" }}>
                <h3 style={{ marginTop: "1rem" }}>Custom Dimensions</h3>
                <select
                  value={settings.customDefinitionsMode || "include"}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      customDefinitionsMode: e.target.value,
                    }))
                  }
                  disabled={!canEdit}
                  style={{ marginBottom: "0.5rem" }}
                >
                  <option value="match">Match Template</option>
                  <option value="include">Include Template Items</option>
                </select>
                {(settings.customDefinitions || []).map((def, idx) => (
                  <div key={idx} style={{ marginBottom: "0.5rem" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      <select
                        value={def.type}
                        onChange={(e) => {
                          const list = [...(settings.customDefinitions || [])]
                          list[idx] = {
                            ...def,
                            type: e.target.value,
                          }
                          setSettings((prev) => ({
                            ...prev,
                            customDefinitions: list,
                          }))
                        }}
                        disabled={!canEdit}
                        style={{ minWidth: "150px" }}
                      >
                        <option value="dimension">Custom Dimension</option>
                        <option value="metric">Custom Metric</option>
                      </select>
                      {def.type === "dimension" ? (
                        <>
                          {(() => {
                            const parameterListId = `custom-dimension-parameter-${idx}`
                            const query = (def.parameterName || "")
                              .toLowerCase()
                              .trim()
                            const suggestionOptions = (
                              query
                                ? organizationDimensionSuggestions.filter((item) => {
                                    const param =
                                      typeof item.parameterName === "string"
                                        ? item.parameterName.toLowerCase()
                                        : ""
                                    const display =
                                      typeof item.displayName === "string"
                                        ? item.displayName.toLowerCase()
                                        : ""
                                    return (
                                      param.includes(query) || display.includes(query)
                                    )
                                  })
                                : organizationDimensionSuggestions
                            ).slice(0, 25)
                            return (
                              <>
                                <input
                                  type="text"
                                  placeholder="parameterName"
                                  value={def.parameterName}
                                  list={parameterListId}
                                  onChange={(e) => {
                                    const value = e.target.value
                                    const list = [...(settings.customDefinitions || [])]
                                    const suggestion = organizationDimensionSuggestions.find(
                                      (item) =>
                                        item.parameterName?.toLowerCase() ===
                                        value.toLowerCase()
                                    )
                                    const updated = {
                                      ...def,
                                      parameterName: value,
                                    }
                                    if (suggestion) {
                                      if (!updated.displayName && suggestion.displayName)
                                        updated.displayName = suggestion.displayName
                                      if (!updated.description && suggestion.description)
                                        updated.description = suggestion.description
                                      if (suggestion.scope)
                                        updated.scope = suggestion.scope
                                    }
                                    list[idx] = updated
                                    setSettings((prev) => ({
                                      ...prev,
                                      customDefinitions: list,
                                    }))
                                  }}
                                  disabled={!canEdit}
                                />
                                <datalist id={parameterListId}>
                                  {suggestionOptions.map((item) => (
                                    <option
                                      key={item.parameterName}
                                      value={item.parameterName}
                                      label={
                                        item.displayName
                                          ? `${item.parameterName} — ${item.displayName}`
                                          : item.parameterName
                                      }
                                    />
                                  ))}
                                </datalist>
                              </>
                            )
                          })()}
                          <input
                            type="text"
                            placeholder="displayName"
                            value={def.displayName}
                            onChange={(e) => {
                              const list = [...(settings.customDefinitions || [])]
                              list[idx] = {
                                ...def,
                                displayName: e.target.value,
                              }
                              setSettings((prev) => ({
                                ...prev,
                                customDefinitions: list,
                              }))
                            }}
                            disabled={!canEdit}
                          />
                          <input
                            type="text"
                            placeholder="description"
                            value={def.description}
                            onChange={(e) => {
                              const list = [...(settings.customDefinitions || [])]
                              list[idx] = {
                                ...def,
                                description: e.target.value,
                              }
                              setSettings((prev) => ({
                                ...prev,
                                customDefinitions: list,
                              }))
                            }}
                            disabled={!canEdit}
                          />
                          <select
                            value={def.scope}
                            onChange={(e) => {
                              const list = [...(settings.customDefinitions || [])]
                              list[idx] = {
                                ...def,
                                scope: e.target.value,
                              }
                              setSettings((prev) => ({
                                ...prev,
                                customDefinitions: list,
                              }))
                            }}
                            disabled={!canEdit}
                          >
                            <option value="EVENT">EVENT</option>
                            <option value="USER">USER</option>
                          </select>
                        </>
                      ) : (
                        <>
                          {(() => {
                            const parameterListId = `custom-metric-parameter-${idx}`
                            const query = (def.parameterName || "")
                              .toLowerCase()
                              .trim()
                            const suggestionOptions = (
                              query
                                ? organizationMetricSuggestions.filter((item) => {
                                    const param =
                                      typeof item.parameterName === "string"
                                        ? item.parameterName.toLowerCase()
                                        : ""
                                    const display =
                                      typeof item.displayName === "string"
                                        ? item.displayName.toLowerCase()
                                        : ""
                                    return (
                                      param.includes(query) || display.includes(query)
                                    )
                                  })
                                : organizationMetricSuggestions
                            ).slice(0, 25)
                            return (
                              <>
                                <input
                                  type="text"
                                  placeholder="parameterName"
                                  value={def.parameterName}
                                  list={parameterListId}
                                  onChange={(e) => {
                                    const value = e.target.value
                                    const list = [...(settings.customDefinitions || [])]
                                    const suggestion = organizationMetricSuggestions.find(
                                      (item) =>
                                        item.parameterName?.toLowerCase() ===
                                        value.toLowerCase()
                                    )
                                    const updated = {
                                      ...def,
                                      parameterName: value,
                                    }
                                    if (suggestion) {
                                      if (!updated.displayName && suggestion.displayName)
                                        updated.displayName = suggestion.displayName
                                      if (!updated.description && suggestion.description)
                                        updated.description = suggestion.description
                                      if (
                                        suggestion.measurementUnit &&
                                        (!updated.measurementUnit ||
                                          updated.measurementUnit ===
                                            "MEASUREMENT_UNIT_UNSPECIFIED")
                                      ) {
                                        updated.measurementUnit = suggestion.measurementUnit
                                      }
                                      if (
                                        suggestion.restrictedMetricType &&
                                        (!updated.restrictedMetricType ||
                                          updated.restrictedMetricType ===
                                            "RESTRICTED_METRIC_TYPE_UNSPECIFIED")
                                      ) {
                                        updated.restrictedMetricType =
                                          suggestion.restrictedMetricType
                                      }
                                    }
                                    list[idx] = updated
                                    setSettings((prev) => ({
                                      ...prev,
                                      customDefinitions: list,
                                    }))
                                  }}
                                  disabled={!canEdit}
                                />
                                <datalist id={parameterListId}>
                                  {suggestionOptions.map((item) => (
                                    <option
                                      key={item.parameterName}
                                      value={item.parameterName}
                                      label={
                                        item.displayName
                                          ? `${item.parameterName} — ${item.displayName}`
                                          : item.parameterName
                                      }
                                    />
                                  ))}
                                </datalist>
                              </>
                            )
                          })()}
                          <input
                            type="text"
                            placeholder="displayName"
                            value={def.displayName}
                            onChange={(e) => {
                              const list = [...(settings.customDefinitions || [])]
                              list[idx] = {
                                ...def,
                                displayName: e.target.value,
                              }
                              setSettings((prev) => ({
                                ...prev,
                                customDefinitions: list,
                              }))
                            }}
                            disabled={!canEdit}
                          />
                          <input
                            type="text"
                            placeholder="description"
                            value={def.description}
                            onChange={(e) => {
                              const list = [...(settings.customDefinitions || [])]
                              list[idx] = {
                                ...def,
                                description: e.target.value,
                              }
                              setSettings((prev) => ({
                                ...prev,
                                customDefinitions: list,
                              }))
                            }}
                            disabled={!canEdit}
                          />
                          <select
                            value={def.measurementUnit}
                            onChange={(e) => {
                              const list = [...(settings.customDefinitions || [])]
                              list[idx] = {
                                ...def,
                                measurementUnit: e.target.value,
                              }
                              setSettings((prev) => ({
                                ...prev,
                                customDefinitions: list,
                              }))
                            }}
                            disabled={!canEdit}
                          >
                            <option value="MEASUREMENT_UNIT_UNSPECIFIED">
                              MEASUREMENT_UNIT_UNSPECIFIED
                            </option>
                            <option value="STANDARD">STANDARD</option>
                            <option value="CURRENCY">CURRENCY</option>
                            <option value="FEET">FEET</option>
                            <option value="METERS">METERS</option>
                            <option value="KILOMETERS">KILOMETERS</option>
                            <option value="MILES">MILES</option>
                            <option value="MILLISECONDS">MILLISECONDS</option>
                            <option value="SECONDS">SECONDS</option>
                            <option value="MINUTES">MINUTES</option>
                            <option value="HOURS">HOURS</option>
                          </select>
                          <select
                            value={
                              def.restrictedMetricType ||
                              "RESTRICTED_METRIC_TYPE_UNSPECIFIED"
                            }
                            onChange={(e) => {
                              const list = [...(settings.customDefinitions || [])]
                              list[idx] = {
                                ...def,
                                restrictedMetricType: e.target.value,
                              }
                              setSettings((prev) => ({
                                ...prev,
                                customDefinitions: list,
                              }))
                            }}
                            disabled={!canEdit}
                          >
                            <option value="RESTRICTED_METRIC_TYPE_UNSPECIFIED">
                              RESTRICTED_METRIC_TYPE_UNSPECIFIED
                            </option>
                            <option value="COST_DATA">COST_DATA</option>
                            <option value="REVENUE_DATA">REVENUE_DATA</option>
                          </select>
                        </>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => {
                            const list = [...(settings.customDefinitions || [])]
                            list.splice(idx, 1)
                            setSettings((prev) => ({
                              ...prev,
                              customDefinitions: list,
                            }))
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {canEdit && (
                  <button
                    onClick={() =>
                      setSettings((prev) => ({
                        ...prev,
                        customDefinitions: [
                          ...(prev.customDefinitions || []),
                          {
                            type: "dimension",
                            parameterName: "",
                            displayName: "",
                            description: "",
                            scope: "EVENT",
                          },
                        ],
                      }))
                    }
                  >
                    Add Custom Definition
                  </button>
                )}
              </div>
              <div style={{ display: currentStep === 5 ? "block" : "none" }}>
                <h3 style={{ marginTop: "1rem" }}>Key Events</h3>
                <select
                  value={settings.keyEventsMode || "include"}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      keyEventsMode: e.target.value,
                    }))
                  }
                  disabled={!canEdit}
                  style={{ marginBottom: "0.5rem" }}
                >
                  <option value="match">Match Template</option>
                  <option value="include">Include Template Items</option>
                </select>
                {(settings.keyEvents || []).map((evt, idx) => (
                  <div key={idx} style={{ marginBottom: "0.5rem" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        flexWrap: "wrap",
                      }}
                    >
                      {(() => {
                        const eventListId = `key-event-name-${idx}`
                        const query = (evt.eventName || "")
                          .toLowerCase()
                          .trim()
                        const suggestionOptions = (
                          query
                            ? organizationKeyEventSuggestions.filter((item) => {
                                const name =
                                  typeof item.eventName === "string"
                                    ? item.eventName.toLowerCase()
                                    : ""
                                return name.includes(query)
                              })
                            : organizationKeyEventSuggestions
                        ).slice(0, 25)
                        return (
                          <>
                            <input
                              type="text"
                              placeholder="eventName"
                              value={evt.eventName}
                              list={eventListId}
                              onChange={(e) => {
                                const value = e.target.value
                                const list = [...(settings.keyEvents || [])]
                                const suggestion = organizationKeyEventSuggestions.find(
                                  (item) => item.eventName?.toLowerCase() === value.toLowerCase()
                                )
                                const updated = {
                                  ...evt,
                                  eventName: value,
                                }
                                if (suggestion) {
                                  if (
                                    suggestion.countingMethod &&
                                    (!updated.countingMethod ||
                                      updated.countingMethod ===
                                        "COUNTING_METHOD_UNSPECIFIED")
                                  ) {
                                    updated.countingMethod = suggestion.countingMethod
                                  }
                                  if (suggestion.defaultValue) {
                                    const nextDefault = {
                                      ...(updated.defaultValue || {}),
                                    }
                                    if (
                                      typeof suggestion.defaultValue.numericValue === "number" &&
                                      Number.isFinite(suggestion.defaultValue.numericValue) &&
                                      nextDefault.numericValue == null
                                    ) {
                                      nextDefault.numericValue =
                                        suggestion.defaultValue.numericValue
                                    }
                                    if (
                                      suggestion.defaultValue.currencyCode &&
                                      !nextDefault.currencyCode
                                    ) {
                                      nextDefault.currencyCode =
                                        suggestion.defaultValue.currencyCode
                                    }
                                    updated.defaultValue = nextDefault
                                  }
                                }
                                list[idx] = updated
                                setSettings((prev) => ({
                                  ...prev,
                                  keyEvents: list,
                                }))
                              }}
                              disabled={!canEdit}
                            />
                            <datalist id={eventListId}>
                              {suggestionOptions.map((item) => (
                                <option key={item.eventName} value={item.eventName} />
                              ))}
                            </datalist>
                          </>
                        )
                      })()}
                      <select
                        value={evt.countingMethod}
                        onChange={(e) => {
                          const list = [...(settings.keyEvents || [])]
                          list[idx] = {
                            ...evt,
                            countingMethod: e.target.value,
                          }
                          setSettings((prev) => ({
                            ...prev,
                            keyEvents: list,
                          }))
                        }}
                        disabled={!canEdit}
                      >
                        {countingMethodOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        placeholder="keyEventValue"
                        title="Key event value"
                        value={evt.defaultValue?.numericValue ?? ""}
                        onChange={(e) => {
                          const list = [...(settings.keyEvents || [])]
                          const dv = { ...(evt.defaultValue || {}) }
                          if (e.target.value === "") {
                            delete dv.numericValue
                          } else {
                            dv.numericValue = parseFloat(e.target.value)
                          }
                          list[idx] = {
                            ...evt,
                            defaultValue: dv,
                          }
                          setSettings((prev) => ({
                            ...prev,
                            keyEvents: list,
                          }))
                        }}
                        disabled={!canEdit}
                        style={{ maxWidth: "120px" }}
                      />
                      <select
                        value={evt.defaultValue?.currencyCode || ""}
                        onChange={(e) => {
                          const list = [...(settings.keyEvents || [])]
                          list[idx] = {
                            ...evt,
                            defaultValue: {
                              ...(evt.defaultValue || {}),
                              currencyCode: e.target.value,
                            },
                          }
                          setSettings((prev) => ({
                            ...prev,
                            keyEvents: list,
                          }))
                        }}
                        disabled={!canEdit}
                      >
                        {currencyOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      {canEdit && (
                        <button
                          onClick={() => {
                            const list = [...(settings.keyEvents || [])]
                            list.splice(idx, 1)
                            setSettings((prev) => ({
                              ...prev,
                              keyEvents: list,
                            }))
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {canEdit && (
                  <button
                    onClick={() =>
                      setSettings((prev) => ({
                        ...prev,
                        keyEvents: [
                          ...(prev.keyEvents || []),
                          {
                            eventName: "",
                            countingMethod: "COUNTING_METHOD_UNSPECIFIED",
                            defaultValue: {
                              currencyCode: prev.currencyCode?.value || "USD",
                            },
                          },
                        ],
                      }))
                    }
                  >
                    Add Key Event
                  </button>
                )}
              </div>
              <div
                className="custom-event-sections"
                style={{ display: currentStep === 6 ? "block" : "none" }}
              >
                <section className="custom-event-section ui-stack ui-stack--lg">
                  <div className="custom-event-section__header">
                    <h3 className="ui-heading-sm">Custom Event Create Rules</h3>
                    <p className="custom-event-section__description">
                      Configure how GA4 should create new custom events when incoming
                      events match the conditions below.
                    </p>
                  </div>
                  <div className="custom-event-mode">
                    <label className="ui-label" htmlFor="event-create-rules-mode">
                      Comparison mode
                    </label>
                    <select
                      id="event-create-rules-mode"
                      className="ui-select"
                      value={settings.eventCreateRulesMode || "include"}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          eventCreateRulesMode: e.target.value,
                        }))
                      }
                      disabled={!canEdit}
                    >
                      <option value="match">Match Template</option>
                      <option value="include">Include Template Items</option>
                    </select>
                  </div>
                  <div className="ui-stack ui-stack--lg">
                    {(settings.eventCreateRules || []).length ? (
                      (settings.eventCreateRules || []).map((rule, idx) => {
                        const baseId = `event-create-${idx}`
                        const matchingConditions = Array.isArray(rule.matchingConditions)
                          ? rule.matchingConditions
                          : []
                        const parameterMutations = Array.isArray(rule.parameterMutations)
                          ? rule.parameterMutations
                          : []
                        const handleConditionChange = (conditionIdx, updates) => {
                          updateCreateRule(idx, (currentRule) => {
                            const nextConditions = Array.isArray(currentRule.matchingConditions)
                              ? currentRule.matchingConditions.map((item) => ({ ...item }))
                              : []
                            while (nextConditions.length <= conditionIdx) {
                              nextConditions.push(createDefaultMatchingCondition())
                            }
                            const updatedCondition = {
                              ...(nextConditions[conditionIdx] || createDefaultMatchingCondition()),
                              ...updates,
                            }
                            if (typeof updatedCondition.matchType === "string") {
                              const normalizedMatchType = updatedCondition.matchType.toUpperCase()
                              updatedCondition.matchType = VALID_MATCH_TYPES.has(normalizedMatchType)
                                ? normalizedMatchType
                                : "MATCH_TYPE_UNSPECIFIED"
                            }
                            if (
                              updatedCondition.caseSensitive !== true &&
                              updatedCondition.caseSensitive !== false
                            ) {
                              delete updatedCondition.caseSensitive
                            }
                            if (
                              typeof updatedCondition.value !== "string" &&
                              updatedCondition.value != null
                            ) {
                              updatedCondition.value = String(updatedCondition.value)
                            }
                            nextConditions[conditionIdx] = updatedCondition
                            const nextRule = { ...currentRule }
                            if (nextConditions.length) nextRule.matchingConditions = nextConditions
                            else delete nextRule.matchingConditions
                            const derived = deriveSourceEventFromConditions(nextConditions)
                            if (derived) nextRule.sourceEvent = derived
                            else delete nextRule.sourceEvent
                            return nextRule
                          })
                        }
                        const handleRemoveCondition = (conditionIdx) => {
                          updateCreateRule(idx, (currentRule) => {
                            const nextConditions = Array.isArray(currentRule.matchingConditions)
                              ? currentRule.matchingConditions.filter((_, i) => i !== conditionIdx)
                              : []
                            const nextRule = { ...currentRule }
                            if (nextConditions.length) nextRule.matchingConditions = nextConditions
                            else delete nextRule.matchingConditions
                            const derived = deriveSourceEventFromConditions(nextConditions)
                            if (derived) nextRule.sourceEvent = derived
                            else delete nextRule.sourceEvent
                            return nextRule
                          })
                        }
                        const handleAddCondition = () => {
                          updateCreateRule(idx, (currentRule) => {
                            const nextConditions = Array.isArray(currentRule.matchingConditions)
                              ? currentRule.matchingConditions.map((item) => ({ ...item }))
                              : []
                            nextConditions.push(
                              createInitialMatchingCondition(nextConditions.length === 0)
                            )
                            const nextRule = { ...currentRule, matchingConditions: nextConditions }
                            const derived = deriveSourceEventFromConditions(nextConditions)
                            if (derived) nextRule.sourceEvent = derived
                            else delete nextRule.sourceEvent
                            return nextRule
                          })
                        }
                        const handleMutationChange = (mutationIdx, updates) => {
                          updateCreateRule(idx, (currentRule) => {
                            const nextMutations = Array.isArray(currentRule.parameterMutations)
                              ? currentRule.parameterMutations.map((item) => ({ ...item }))
                              : []
                            while (nextMutations.length <= mutationIdx) {
                              nextMutations.push(createDefaultParameterMutation())
                            }
                            const updatedMutation = {
                              ...(nextMutations[mutationIdx] || createDefaultParameterMutation()),
                              ...updates,
                            }
                            const operation = (updatedMutation.operation || "SET_VALUE").toUpperCase()
                            updatedMutation.operation = VALID_PARAMETER_OPERATIONS.has(operation)
                              ? operation
                              : "SET_VALUE"
                            if (updatedMutation.operation === "COPY_PARAMETER") {
                              if (typeof updatedMutation.copyFromParameter !== "string") {
                                updatedMutation.copyFromParameter = ""
                              }
                              delete updatedMutation.value
                              delete updatedMutation.valueType
                            } else {
                              const type = (updatedMutation.valueType || "STRING").toUpperCase()
                              updatedMutation.valueType = VALID_PARAMETER_VALUE_TYPES.has(type)
                                ? type
                                : "STRING"
                              if (updatedMutation.valueType === "BOOLEAN") {
                                const boolValue = toNormalizedBoolean(updatedMutation.value)
                                updatedMutation.value =
                                  boolValue === null ? "" : boolValue ? "true" : "false"
                              } else if (updatedMutation.value == null) {
                                updatedMutation.value = ""
                              } else if (typeof updatedMutation.value !== "string") {
                                updatedMutation.value = String(updatedMutation.value)
                              }
                              delete updatedMutation.copyFromParameter
                            }
                            nextMutations[mutationIdx] = updatedMutation
                            const nextRule = { ...currentRule }
                            if (nextMutations.length) nextRule.parameterMutations = nextMutations
                            else delete nextRule.parameterMutations
                            return nextRule
                          })
                        }
                        const handleRemoveMutation = (mutationIdx) => {
                          updateCreateRule(idx, (currentRule) => {
                            const nextMutations = Array.isArray(currentRule.parameterMutations)
                              ? currentRule.parameterMutations.filter((_, i) => i !== mutationIdx)
                              : []
                            const nextRule = { ...currentRule }
                            if (nextMutations.length) nextRule.parameterMutations = nextMutations
                            else delete nextRule.parameterMutations
                            return nextRule
                          })
                        }
                        const handleAddMutation = () => {
                          updateCreateRule(idx, (currentRule) => {
                            const nextMutations = Array.isArray(currentRule.parameterMutations)
                              ? currentRule.parameterMutations.map((item) => ({ ...item }))
                              : []
                            nextMutations.push(createDefaultParameterMutation())
                            return { ...currentRule, parameterMutations: nextMutations }
                          })
                        }
                        return (
                          <div
                            key={`create-rule-${idx}`}
                            className="custom-event-rule ui-stack ui-stack--lg"
                          >
                            <div className="custom-event-card__header">
                              <div className="custom-event-card__primary">
                                <Input
                                  id={`${baseId}-destination-event`}
                                  label="Custom event name"
                                  placeholder="Destination event name"
                                  value={rule.destinationEvent || ""}
                                  onChange={(e) =>
                                    updateCreateRule(idx, (currentRule) => ({
                                      ...currentRule,
                                      destinationEvent: e.target.value,
                                    }))
                                  }
                                  disabled={!canEdit}
                                />
                              </div>
                            </div>
                            <div className="custom-event-rule__section" id={`${baseId}-matching`}>
                              <div className="custom-event-rule__section-header">
                                <h4 className="ui-heading-xs">Matching conditions</h4>
                                <p className="custom-event-rule__section-description">
                                  Create a custom event when another event matches ALL of the
                                  following conditions.
                                </p>
                              </div>
                              <div className="custom-event-table" role="group">
                                <div className="custom-event-table__row custom-event-table__row--header">
                                  <span>Parameter</span>
                                  <span>Operator</span>
                                  <span>Value</span>
                                  <span aria-hidden="true" />
                                </div>
                                {matchingConditions.length ? (
                                  matchingConditions.map((condition, conditionIdx) => {
                                    const operatorValue = (() => {
                                      const matchType = (condition.matchType || "EXACT").toUpperCase()
                                      const caseSensitive =
                                        typeof condition.caseSensitive === "boolean"
                                          ? condition.caseSensitive
                                          : true
                                      const candidate = `${matchType}|${caseSensitive}`
                                      return MATCHING_OPERATOR_OPTIONS.some(
                                        (option) => option.value === candidate
                                      )
                                        ? candidate
                                        : MATCHING_OPERATOR_OPTIONS[0].value
                                    })()
                                    const parameterListId =
                                      `${baseId}-condition-${conditionIdx}-parameter-list`
                                    return (
                                      <div
                                        key={`${baseId}-condition-${conditionIdx}`}
                                        className="custom-event-table__row"
                                      >
                                        <Input
                                          id={`${baseId}-condition-${conditionIdx}-parameter`}
                                          label="Event parameter"
                                          hideLabel={conditionIdx !== 0}
                                          placeholder="e.g. event_name"
                                          value={condition.eventParameter || ""}
                                          list={parameterListId}
                                          onChange={(e) =>
                                            handleConditionChange(conditionIdx, {
                                              eventParameter: e.target.value,
                                            })
                                          }
                                          disabled={!canEdit}
                                        />
                                        <datalist id={parameterListId}>
                                          {eventParameterSuggestions.map((option) => (
                                            <option
                                              key={`${parameterListId}-${option.parameterName}`}
                                              value={option.parameterName}
                                              label={formatEventParameterSuggestionLabel(option)}
                                            />
                                          ))}
                                        </datalist>
                                        <div className="custom-event-table__cell custom-event-table__cell--operator">
                                          {conditionIdx === 0 && (
                                            <label
                                              className="ui-label"
                                              htmlFor={`${baseId}-condition-${conditionIdx}-operator`}
                                            >
                                              Operator
                                            </label>
                                          )}
                                          <select
                                            id={`${baseId}-condition-${conditionIdx}-operator`}
                                            className="ui-select"
                                            value={operatorValue}
                                            onChange={(e) => {
                                              const [matchType, caseFlag] = e.target.value.split("|")
                                              handleConditionChange(conditionIdx, {
                                                matchType,
                                                caseSensitive: caseFlag === "true",
                                              })
                                            }}
                                            disabled={!canEdit}
                                          >
                                            {MATCHING_OPERATOR_OPTIONS.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        <Input
                                          id={`${baseId}-condition-${conditionIdx}-value`}
                                          label="Value"
                                          hideLabel={conditionIdx !== 0}
                                          placeholder="Value"
                                          value={condition.value || ""}
                                          onChange={(e) =>
                                            handleConditionChange(conditionIdx, {
                                              value: e.target.value,
                                            })
                                          }
                                          disabled={!canEdit}
                                        />
                                        {canEdit && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="custom-event-table__remove"
                                            onClick={() => handleRemoveCondition(conditionIdx)}
                                          >
                                            Remove
                                          </Button>
                                        )}
                                      </div>
                                    )
                                  })
                                ) : (
                                  <div className="custom-event-empty">No conditions configured.</div>
                                )}
                              </div>
                              {canEdit && (
                                <Button variant="secondary" size="sm" onClick={handleAddCondition}>
                                  Add condition
                                </Button>
                              )}
                            </div>
                            <div className="custom-event-rule__section">
                              <div className="custom-event-rule__section-header">
                                <h4 className="ui-heading-xs">Parameter configuration</h4>
                                <p className="custom-event-rule__section-description">
                                  Update event parameters on the new custom event.
                                </p>
                              </div>
                              <Checkbox
                                id={`${baseId}-copy-parameters`}
                                label="Copy parameters"
                                description="Copy all parameters from the source event to the custom event."
                                checked={Boolean(rule.copyParameters)}
                                onChange={(e) =>
                                  updateCreateRule(idx, (currentRule) => {
                                    const next = { ...currentRule }
                                    if (e.target.checked) next.copyParameters = true
                                    else delete next.copyParameters
                                    return next
                                  })
                                }
                                disabled={!canEdit}
                              />
                              <div className="custom-event-table" role="group">
                                <div className="custom-event-table__row custom-event-table__row--header">
                                  <span>Parameter</span>
                                  <span>Operation</span>
                                  <span>Type / Source</span>
                                  <span>Value</span>
                                  <span aria-hidden="true" />
                                </div>
                                {parameterMutations.length ? (
                                  parameterMutations.map((mutation, mutationIdx) => {
                                    const operation = mutation.operation || "SET_VALUE"
                                    const valueType = mutation.valueType || "STRING"
                                    return (
                                      <div
                                        key={`${baseId}-mutation-${mutationIdx}`}
                                        className="custom-event-table__row"
                                      >
                                        <Input
                                          id={`${baseId}-mutation-${mutationIdx}-parameter`}
                                          label="Parameter"
                                          hideLabel={mutationIdx !== 0}
                                          placeholder="Parameter name"
                                          value={mutation.parameter || ""}
                                          onChange={(e) =>
                                            handleMutationChange(mutationIdx, {
                                              parameter: e.target.value,
                                            })
                                          }
                                          disabled={!canEdit}
                                        />
                                        <div className="custom-event-table__cell custom-event-table__cell--operator">
                                          {mutationIdx === 0 && (
                                            <label
                                              className="ui-label"
                                              htmlFor={`${baseId}-mutation-${mutationIdx}-operation`}
                                            >
                                              Operation
                                            </label>
                                          )}
                                          <select
                                            id={`${baseId}-mutation-${mutationIdx}-operation`}
                                            className="ui-select"
                                            value={operation}
                                            onChange={(e) =>
                                              handleMutationChange(mutationIdx, {
                                                operation: e.target.value,
                                              })
                                            }
                                            disabled={!canEdit}
                                          >
                                            {PARAMETER_MUTATION_OPERATIONS.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        {operation === "COPY_PARAMETER" ? (
                                          <>
                                            <Input
                                              id={`${baseId}-mutation-${mutationIdx}-source`}
                                              label="Source parameter"
                                              hideLabel={mutationIdx !== 0}
                                              placeholder="Event parameter to copy"
                                              value={mutation.copyFromParameter || ""}
                                              onChange={(e) =>
                                                handleMutationChange(mutationIdx, {
                                                  copyFromParameter: e.target.value,
                                                })
                                              }
                                              disabled={!canEdit}
                                            />
                                            <div className="custom-event-table__cell custom-event-table__cell--placeholder">
                                              {mutationIdx === 0 && (
                                                <span className="custom-event-table__placeholder-label">
                                                  Value
                                                </span>
                                              )}
                                              <span className="custom-event-table__placeholder-text">
                                                Copied from source
                                              </span>
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <div className="custom-event-table__cell custom-event-table__cell--operator">
                                              {mutationIdx === 0 && (
                                                <label
                                                  className="ui-label"
                                                  htmlFor={`${baseId}-mutation-${mutationIdx}-value-type`}
                                                >
                                                  Value type
                                                </label>
                                              )}
                                              <select
                                                id={`${baseId}-mutation-${mutationIdx}-value-type`}
                                                className="ui-select"
                                                value={valueType}
                                                onChange={(e) =>
                                                  handleMutationChange(mutationIdx, {
                                                    valueType: e.target.value,
                                                  })
                                                }
                                                disabled={!canEdit}
                                              >
                                                {PARAMETER_VALUE_TYPE_OPTIONS.map((option) => (
                                                  <option key={option.value} value={option.value}>
                                                    {option.label}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                            {valueType === "BOOLEAN" ? (
                                              <div className="custom-event-table__cell">
                                                {mutationIdx === 0 && (
                                                  <label
                                                    className="ui-label"
                                                    htmlFor={`${baseId}-mutation-${mutationIdx}-value`}
                                                  >
                                                    Value
                                                  </label>
                                                )}
                                                <select
                                                  id={`${baseId}-mutation-${mutationIdx}-value`}
                                                  className="ui-select"
                                                  value={mutation.value ?? ""}
                                                  onChange={(e) =>
                                                    handleMutationChange(mutationIdx, {
                                                      value: e.target.value,
                                                    })
                                                  }
                                                  disabled={!canEdit}
                                                >
                                                  {BOOLEAN_VALUE_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                      {option.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            ) : (
                                              <Input
                                                id={`${baseId}-mutation-${mutationIdx}-value`}
                                                label="Value"
                                                hideLabel={mutationIdx !== 0}
                                                placeholder="Value"
                                                value={mutation.value ?? ""}
                                                onChange={(e) =>
                                                  handleMutationChange(mutationIdx, {
                                                    value: e.target.value,
                                                  })
                                                }
                                                disabled={!canEdit}
                                              />
                                            )}
                                          </>
                                        )}
                                        {canEdit && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="custom-event-table__remove"
                                            onClick={() => handleRemoveMutation(mutationIdx)}
                                          >
                                            Remove
                                          </Button>
                                        )}
                                      </div>
                                    )
                                  })
                                ) : (
                                  <div className="custom-event-empty">
                                    No parameter updates configured.
                                  </div>
                                )}
                              </div>
                              {canEdit && (
                                <Button variant="secondary" size="sm" onClick={handleAddMutation}>
                                  Add parameter
                                </Button>
                              )}
                            </div>
                            <div className="custom-event-rule__footer">
                              <Input
                                id={`${baseId}-description`}
                                className="custom-event-rule__description"
                                label="Description"
                                placeholder="Description"
                                value={rule.description || ""}
                                onChange={(e) => {
                                  const value = e.target.value
                                  updateCreateRule(idx, (currentRule) => {
                                    const next = { ...currentRule }
                                    if (value.trim()) next.description = value
                                    else delete next.description
                                    return next
                                  })
                                }}
                                disabled={!canEdit}
                              />
                              {canEdit && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => updateCreateRule(idx, () => null)}
                                >
                                  Remove rule
                                </Button>
                              )}
                            </div>
                          </div>
                        )
                      })
                    ) : (
                      <p className="custom-event-empty">
                        No custom event create rules configured yet.
                      </p>
                    )}
                  </div>
                  {canEdit && (
                    <Button variant="secondary" onClick={addCreateRule}>
                      Add Create Rule
                    </Button>
                  )}
                </section>
                <section className="custom-event-section ui-stack ui-stack--lg">
                  <div className="custom-event-section__header">
                    <h3 className="ui-heading-sm">Custom Event Modify Rules</h3>
                    <p className="custom-event-section__description">
                      Mirror the GA4 interface for modifying events by adjusting
                      matching conditions and parameter updates.
                    </p>
                  </div>
                  <div className="custom-event-mode">
                    <label className="ui-label" htmlFor="event-edit-rules-mode">
                      Comparison mode
                    </label>
                    <select
                      id="event-edit-rules-mode"
                      className="ui-select"
                      value={settings.eventEditRulesMode || "include"}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          eventEditRulesMode: e.target.value,
                        }))
                      }
                      disabled={!canEdit}
                    >
                      <option value="match">Match Template</option>
                      <option value="include">Include Template Items</option>
                    </select>
                  </div>
                  <div className="ui-stack ui-stack--lg">
                    {(settings.eventEditRules || []).length ? (
                      (settings.eventEditRules || []).map((rule, idx) => {
                        const baseId = `event-edit-${idx}`
                        const matchingConditions = Array.isArray(rule.matchingConditions)
                          ? rule.matchingConditions
                          : []
                        const parameterMutations = Array.isArray(rule.parameterMutations)
                          ? rule.parameterMutations
                          : []
                        const handleConditionChange = (conditionIdx, updates) => {
                          updateEditRule(idx, (currentRule) => {
                            const nextConditions = Array.isArray(currentRule.matchingConditions)
                              ? currentRule.matchingConditions.map((item) => ({ ...item }))
                              : []
                            while (nextConditions.length <= conditionIdx) {
                              nextConditions.push(createDefaultMatchingCondition())
                            }
                            const updatedCondition = {
                              ...(nextConditions[conditionIdx] || createDefaultMatchingCondition()),
                              ...updates,
                            }
                            if (typeof updatedCondition.matchType === "string") {
                              const normalizedMatchType = updatedCondition.matchType.toUpperCase()
                              updatedCondition.matchType = VALID_MATCH_TYPES.has(normalizedMatchType)
                                ? normalizedMatchType
                                : "MATCH_TYPE_UNSPECIFIED"
                            }
                            if (
                              updatedCondition.caseSensitive !== true &&
                              updatedCondition.caseSensitive !== false
                            ) {
                              delete updatedCondition.caseSensitive
                            }
                            if (
                              typeof updatedCondition.value !== "string" &&
                              updatedCondition.value != null
                            ) {
                              updatedCondition.value = String(updatedCondition.value)
                            }
                            nextConditions[conditionIdx] = updatedCondition
                            const nextRule = { ...currentRule }
                            if (nextConditions.length) nextRule.matchingConditions = nextConditions
                            else delete nextRule.matchingConditions
                            return nextRule
                          })
                        }
                        const handleRemoveCondition = (conditionIdx) => {
                          updateEditRule(idx, (currentRule) => {
                            const nextConditions = Array.isArray(currentRule.matchingConditions)
                              ? currentRule.matchingConditions.filter((_, i) => i !== conditionIdx)
                              : []
                            const nextRule = { ...currentRule }
                            if (nextConditions.length) nextRule.matchingConditions = nextConditions
                            else delete nextRule.matchingConditions
                            return nextRule
                          })
                        }
                        const handleAddCondition = () => {
                          updateEditRule(idx, (currentRule) => {
                            const nextConditions = Array.isArray(currentRule.matchingConditions)
                              ? currentRule.matchingConditions.map((item) => ({ ...item }))
                              : []
                            nextConditions.push(
                              createInitialMatchingCondition(nextConditions.length === 0)
                            )
                            return { ...currentRule, matchingConditions: nextConditions }
                          })
                        }
                        const handleMutationChange = (mutationIdx, updates) => {
                          updateEditRule(idx, (currentRule) => {
                            const nextMutations = Array.isArray(currentRule.parameterMutations)
                              ? currentRule.parameterMutations.map((item) => ({ ...item }))
                              : []
                            while (nextMutations.length <= mutationIdx) {
                              nextMutations.push(createDefaultParameterMutation())
                            }
                            const updatedMutation = {
                              ...(nextMutations[mutationIdx] || createDefaultParameterMutation()),
                              ...updates,
                            }
                            const operation = (updatedMutation.operation || "SET_VALUE").toUpperCase()
                            updatedMutation.operation = VALID_PARAMETER_OPERATIONS.has(operation)
                              ? operation
                              : "SET_VALUE"
                            if (updatedMutation.operation === "COPY_PARAMETER") {
                              if (typeof updatedMutation.copyFromParameter !== "string") {
                                updatedMutation.copyFromParameter = ""
                              }
                              delete updatedMutation.value
                              delete updatedMutation.valueType
                            } else {
                              const type = (updatedMutation.valueType || "STRING").toUpperCase()
                              updatedMutation.valueType = VALID_PARAMETER_VALUE_TYPES.has(type)
                                ? type
                                : "STRING"
                              if (updatedMutation.valueType === "BOOLEAN") {
                                const boolValue = toNormalizedBoolean(updatedMutation.value)
                                updatedMutation.value =
                                  boolValue === null ? "" : boolValue ? "true" : "false"
                              } else if (updatedMutation.value == null) {
                                updatedMutation.value = ""
                              } else if (typeof updatedMutation.value !== "string") {
                                updatedMutation.value = String(updatedMutation.value)
                              }
                              delete updatedMutation.copyFromParameter
                            }
                            nextMutations[mutationIdx] = updatedMutation
                            const nextRule = { ...currentRule }
                            if (nextMutations.length) nextRule.parameterMutations = nextMutations
                            else delete nextRule.parameterMutations
                            return nextRule
                          })
                        }
                        const handleRemoveMutation = (mutationIdx) => {
                          updateEditRule(idx, (currentRule) => {
                            const nextMutations = Array.isArray(currentRule.parameterMutations)
                              ? currentRule.parameterMutations.filter((_, i) => i !== mutationIdx)
                              : []
                            const nextRule = { ...currentRule }
                            if (nextMutations.length) nextRule.parameterMutations = nextMutations
                            else delete nextRule.parameterMutations
                            return nextRule
                          })
                        }
                        const handleAddMutation = () => {
                          updateEditRule(idx, (currentRule) => {
                            const nextMutations = Array.isArray(currentRule.parameterMutations)
                              ? currentRule.parameterMutations.map((item) => ({ ...item }))
                              : []
                            nextMutations.push(createDefaultParameterMutation())
                            return { ...currentRule, parameterMutations: nextMutations }
                          })
                        }
                        return (
                          <div key={`edit-rule-${idx}`} className="custom-event-rule ui-stack ui-stack--lg">
                            <div className="custom-event-card__header">
                              <div className="custom-event-card__primary">
                                <Input
                                  id={`${baseId}-event-name`}
                                  label="Event name"
                                  placeholder="Existing event name"
                                  value={rule.eventName || ""}
                                  onChange={(e) =>
                                    updateEditRule(idx, (currentRule) => ({
                                      ...currentRule,
                                      eventName: e.target.value,
                                    }))
                                  }
                                  disabled={!canEdit}
                                />
                              </div>
                            </div>
                            <div className="custom-event-rule__section">
                              <div className="custom-event-rule__section-header">
                                <h4 className="ui-heading-xs">Matching conditions</h4>
                                <p className="custom-event-rule__section-description">
                                  Modify events when they match ALL of the following conditions.
                                </p>
                              </div>
                              <div className="custom-event-table" role="group">
                                <div className="custom-event-table__row custom-event-table__row--header">
                                  <span>Parameter</span>
                                  <span>Operator</span>
                                  <span>Value</span>
                                  <span aria-hidden="true" />
                                </div>
                                {matchingConditions.length ? (
                                  matchingConditions.map((condition, conditionIdx) => {
                                    const operatorValue = (() => {
                                      const matchType = (condition.matchType || "EXACT").toUpperCase()
                                      const caseSensitive =
                                        typeof condition.caseSensitive === "boolean"
                                          ? condition.caseSensitive
                                          : true
                                      const candidate = `${matchType}|${caseSensitive}`
                                      return MATCHING_OPERATOR_OPTIONS.some(
                                        (option) => option.value === candidate
                                      )
                                        ? candidate
                                        : MATCHING_OPERATOR_OPTIONS[0].value
                                    })()
                                    const parameterListId =
                                      `${baseId}-condition-${conditionIdx}-parameter-list`
                                    return (
                                      <div
                                        key={`${baseId}-condition-${conditionIdx}`}
                                        className="custom-event-table__row"
                                      >
                                        <Input
                                          id={`${baseId}-condition-${conditionIdx}-parameter`}
                                          label="Event parameter"
                                          hideLabel={conditionIdx !== 0}
                                          placeholder="e.g. event_name"
                                          value={condition.eventParameter || ""}
                                          list={parameterListId}
                                          onChange={(e) =>
                                            handleConditionChange(conditionIdx, {
                                              eventParameter: e.target.value,
                                            })
                                          }
                                          disabled={!canEdit}
                                        />
                                        <datalist id={parameterListId}>
                                          {eventParameterSuggestions.map((option) => (
                                            <option
                                              key={`${parameterListId}-${option.parameterName}`}
                                              value={option.parameterName}
                                              label={formatEventParameterSuggestionLabel(option)}
                                            />
                                          ))}
                                        </datalist>
                                        <div className="custom-event-table__cell custom-event-table__cell--operator">
                                          {conditionIdx === 0 && (
                                            <label
                                              className="ui-label"
                                              htmlFor={`${baseId}-condition-${conditionIdx}-operator`}
                                            >
                                              Operator
                                            </label>
                                          )}
                                          <select
                                            id={`${baseId}-condition-${conditionIdx}-operator`}
                                            className="ui-select"
                                            value={operatorValue}
                                            onChange={(e) => {
                                              const [matchType, caseFlag] = e.target.value.split("|")
                                              handleConditionChange(conditionIdx, {
                                                matchType,
                                                caseSensitive: caseFlag === "true",
                                              })
                                            }}
                                            disabled={!canEdit}
                                          >
                                            {MATCHING_OPERATOR_OPTIONS.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        <Input
                                          id={`${baseId}-condition-${conditionIdx}-value`}
                                          label="Value"
                                          hideLabel={conditionIdx !== 0}
                                          placeholder="Value"
                                          value={condition.value || ""}
                                          onChange={(e) =>
                                            handleConditionChange(conditionIdx, {
                                              value: e.target.value,
                                            })
                                          }
                                          disabled={!canEdit}
                                        />
                                        {canEdit && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="custom-event-table__remove"
                                            onClick={() => handleRemoveCondition(conditionIdx)}
                                          >
                                            Remove
                                          </Button>
                                        )}
                                      </div>
                                    )
                                  })
                                ) : (
                                  <div className="custom-event-empty">No conditions configured.</div>
                                )}
                              </div>
                              {canEdit && (
                                <Button variant="secondary" size="sm" onClick={handleAddCondition}>
                                  Add condition
                                </Button>
                              )}
                            </div>
                            <div className="custom-event-rule__section">
                              <div className="custom-event-rule__section-header">
                                <h4 className="ui-heading-xs">Parameter configuration</h4>
                                <p className="custom-event-rule__section-description">
                                  Adjust parameters on the modified event.
                                </p>
                              </div>
                              <div className="custom-event-table" role="group">
                                <div className="custom-event-table__row custom-event-table__row--header">
                                  <span>Parameter</span>
                                  <span>Operation</span>
                                  <span>Type / Source</span>
                                  <span>Value</span>
                                  <span aria-hidden="true" />
                                </div>
                                {parameterMutations.length ? (
                                  parameterMutations.map((mutation, mutationIdx) => {
                                    const operation = mutation.operation || "SET_VALUE"
                                    const valueType = mutation.valueType || "STRING"
                                    return (
                                      <div
                                        key={`${baseId}-mutation-${mutationIdx}`}
                                        className="custom-event-table__row"
                                      >
                                        <Input
                                          id={`${baseId}-mutation-${mutationIdx}-parameter`}
                                          label="Parameter"
                                          hideLabel={mutationIdx !== 0}
                                          placeholder="Parameter name"
                                          value={mutation.parameter || ""}
                                          onChange={(e) =>
                                            handleMutationChange(mutationIdx, {
                                              parameter: e.target.value,
                                            })
                                          }
                                          disabled={!canEdit}
                                        />
                                        <div className="custom-event-table__cell custom-event-table__cell--operator">
                                          {mutationIdx === 0 && (
                                            <label
                                              className="ui-label"
                                              htmlFor={`${baseId}-mutation-${mutationIdx}-operation`}
                                            >
                                              Operation
                                            </label>
                                          )}
                                          <select
                                            id={`${baseId}-mutation-${mutationIdx}-operation`}
                                            className="ui-select"
                                            value={operation}
                                            onChange={(e) =>
                                              handleMutationChange(mutationIdx, {
                                                operation: e.target.value,
                                              })
                                            }
                                            disabled={!canEdit}
                                          >
                                            {PARAMETER_MUTATION_OPERATIONS.map((option) => (
                                              <option key={option.value} value={option.value}>
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        {operation === "COPY_PARAMETER" ? (
                                          <>
                                            <Input
                                              id={`${baseId}-mutation-${mutationIdx}-source`}
                                              label="Source parameter"
                                              hideLabel={mutationIdx !== 0}
                                              placeholder="Event parameter to copy"
                                              value={mutation.copyFromParameter || ""}
                                              onChange={(e) =>
                                                handleMutationChange(mutationIdx, {
                                                  copyFromParameter: e.target.value,
                                                })
                                              }
                                              disabled={!canEdit}
                                            />
                                            <div className="custom-event-table__cell custom-event-table__cell--placeholder">
                                              {mutationIdx === 0 && (
                                                <span className="custom-event-table__placeholder-label">
                                                  Value
                                                </span>
                                              )}
                                              <span className="custom-event-table__placeholder-text">
                                                Copied from source
                                              </span>
                                            </div>
                                          </>
                                        ) : (
                                          <>
                                            <div className="custom-event-table__cell custom-event-table__cell--operator">
                                              {mutationIdx === 0 && (
                                                <label
                                                  className="ui-label"
                                                  htmlFor={`${baseId}-mutation-${mutationIdx}-value-type`}
                                                >
                                                  Value type
                                                </label>
                                              )}
                                              <select
                                                id={`${baseId}-mutation-${mutationIdx}-value-type`}
                                                className="ui-select"
                                                value={valueType}
                                                onChange={(e) =>
                                                  handleMutationChange(mutationIdx, {
                                                    valueType: e.target.value,
                                                  })
                                                }
                                                disabled={!canEdit}
                                              >
                                                {PARAMETER_VALUE_TYPE_OPTIONS.map((option) => (
                                                  <option key={option.value} value={option.value}>
                                                    {option.label}
                                                  </option>
                                                ))}
                                              </select>
                                            </div>
                                            {valueType === "BOOLEAN" ? (
                                              <div className="custom-event-table__cell">
                                                {mutationIdx === 0 && (
                                                  <label
                                                    className="ui-label"
                                                    htmlFor={`${baseId}-mutation-${mutationIdx}-value`}
                                                  >
                                                    Value
                                                  </label>
                                                )}
                                                <select
                                                  id={`${baseId}-mutation-${mutationIdx}-value`}
                                                  className="ui-select"
                                                  value={mutation.value ?? ""}
                                                  onChange={(e) =>
                                                    handleMutationChange(mutationIdx, {
                                                      value: e.target.value,
                                                    })
                                                  }
                                                  disabled={!canEdit}
                                                >
                                                  {BOOLEAN_VALUE_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                      {option.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            ) : (
                                              <Input
                                                id={`${baseId}-mutation-${mutationIdx}-value`}
                                                label="Value"
                                                hideLabel={mutationIdx !== 0}
                                                placeholder="Value"
                                                value={mutation.value ?? ""}
                                                onChange={(e) =>
                                                  handleMutationChange(mutationIdx, {
                                                    value: e.target.value,
                                                  })
                                                }
                                                disabled={!canEdit}
                                              />
                                            )}
                                          </>
                                        )}
                                        {canEdit && (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="custom-event-table__remove"
                                            onClick={() => handleRemoveMutation(mutationIdx)}
                                          >
                                            Remove
                                          </Button>
                                        )}
                                      </div>
                                    )
                                  })
                                ) : (
                                  <div className="custom-event-empty">
                                    No parameter updates configured.
                                  </div>
                                )}
                              </div>
                              {canEdit && (
                                <Button variant="secondary" size="sm" onClick={handleAddMutation}>
                                  Add parameter
                                </Button>
                              )}
                            </div>
                            <div className="custom-event-rule__footer">
                              <Input
                                id={`${baseId}-description`}
                                className="custom-event-rule__description"
                                label="Description"
                                placeholder="Description"
                                value={rule.description || ""}
                                onChange={(e) => {
                                  const value = e.target.value
                                  updateEditRule(idx, (currentRule) => {
                                    const next = { ...currentRule }
                                    if (value.trim()) next.description = value
                                    else delete next.description
                                    return next
                                  })
                                }}
                                disabled={!canEdit}
                              />
                              {canEdit && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => updateEditRule(idx, () => null)}
                                >
                                  Remove rule
                                </Button>
                              )}
                            </div>
                          </div>
                        )
                      })
                    ) : (
                      <p className="custom-event-empty">
                        No custom event modify rules configured yet.
                      </p>
                    )}
                  </div>
                  {canEdit && (
                    <Button variant="secondary" onClick={addEditRule}>
                      Add Modify Rule
                    </Button>
                  )}
                </section>
              </div>
              <div style={{ display: currentStep === 7 ? "block" : "none" }}>
                <h3 style={{ marginTop: "1rem" }}>BigQuery Links</h3>
                <p style={{ color: "#475569", marginBottom: "0.75rem" }}>
                  Manage whether GA4 properties should maintain specific BigQuery
                  export links.
                </p>
                <select
                  value={bigQueryAction}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      bigQueryLinksAction: e.target.value,
                    }))
                  }
                  disabled={!canEdit}
                  style={{ marginBottom: "0.75rem" }}
                >
                  <option value="dont-manage">Don't manage BigQuery linking</option>
                  <option value="manage">Manage BigQuery linking</option>
                </select>
                {!isManagingBigQuery ? (
                  <p style={{ color: "#64748b" }}>
                    This template will not enforce any BigQuery linking
                    configuration.
                  </p>
                ) : (
                  <>
                    {bigQueryLinks.length === 0 && (
                      <p style={{ color: "#64748b", marginBottom: "0.75rem" }}>
                        {canEdit
                          ? "Add one or more expected BigQuery links."
                          : "No BigQuery links are required by this template."}
                      </p>
                    )}
                    {bigQueryLinks.map((link, idx) => (
                      <div
                        key={idx}
                        style={{
                          marginBottom: "0.75rem",
                          border: "1px solid var(--border-color, #e2e8f0)",
                          borderRadius: "var(--border-radius)",
                          padding: "0.75rem",
                          backgroundColor: "var(--card-background, #0f172a)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "0.75rem",
                            alignItems: "flex-end",
                          }}
                        >
                          <div style={{ flex: "1 1 220px" }}>
                            <label className="ui-label" htmlFor={`bql-project-${idx}`}>
                              BigQuery project ID
                            </label>
                            <input
                              id={`bql-project-${idx}`}
                              type="text"
                              value={link.project || ""}
                              onChange={(e) =>
                                updateBigQueryLink(idx, (current) => ({
                                  ...current,
                                  project: e.target.value,
                                }))
                              }
                              disabled={!canEdit || !isManagingBigQuery}
                            />
                          </div>
                          <div style={{ flex: "1 1 180px" }}>
                            <label className="ui-label" htmlFor={`bql-daily-${idx}`}>
                              Daily export
                            </label>
                            <select
                              id={`bql-daily-${idx}`}
                              value={toBooleanSelectValue(link.dailyExportEnabled)}
                              onChange={(e) =>
                                updateBigQueryLink(idx, (current) => {
                                  const next = { ...current }
                                  const parsed = fromBooleanSelectValue(
                                    e.target.value
                                  )
                                  if (parsed === undefined)
                                    delete next.dailyExportEnabled
                                  else next.dailyExportEnabled = parsed
                                  return next
                                })
                              }
                              disabled={!canEdit || !isManagingBigQuery}
                            >
                              {bigQueryBooleanOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div style={{ flex: "1 1 180px" }}>
                            <label
                              className="ui-label"
                              htmlFor={`bql-streaming-${idx}`}
                            >
                              Streaming export
                            </label>
                            <select
                              id={`bql-streaming-${idx}`}
                              value={toBooleanSelectValue(
                                link.streamingExportEnabled
                              )}
                              onChange={(e) =>
                                updateBigQueryLink(idx, (current) => {
                                  const next = { ...current }
                                  const parsed = fromBooleanSelectValue(
                                    e.target.value
                                  )
                                  if (parsed === undefined)
                                    delete next.streamingExportEnabled
                                  else next.streamingExportEnabled = parsed
                                  return next
                                })
                              }
                              disabled={!canEdit || !isManagingBigQuery}
                            >
                              {bigQueryBooleanOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div style={{ flex: "1 1 200px" }}>
                            <label
                              className="ui-label"
                              htmlFor={`bql-fresh-${idx}`}
                            >
                              Fresh daily export
                            </label>
                            <select
                              id={`bql-fresh-${idx}`}
                              value={toBooleanSelectValue(
                                link.freshDailyExportEnabled
                              )}
                              onChange={(e) =>
                                updateBigQueryLink(idx, (current) => {
                                  const next = { ...current }
                                  const parsed = fromBooleanSelectValue(
                                    e.target.value
                                  )
                                  if (parsed === undefined)
                                    delete next.freshDailyExportEnabled
                                  else next.freshDailyExportEnabled = parsed
                                  return next
                                })
                              }
                              disabled={!canEdit || !isManagingBigQuery}
                            >
                              {bigQueryBooleanOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div style={{ flex: "1 1 220px" }}>
                            <label
                              className="ui-label"
                              htmlFor={`bql-ads-${idx}`}
                            >
                              Include advertising ID
                            </label>
                            <select
                              id={`bql-ads-${idx}`}
                              value={toBooleanSelectValue(
                                link.includeAdvertisingId
                              )}
                              onChange={(e) =>
                                updateBigQueryLink(idx, (current) => {
                                  const next = { ...current }
                                  const parsed = fromBooleanSelectValue(
                                    e.target.value
                                  )
                                  if (parsed === undefined)
                                    delete next.includeAdvertisingId
                                  else next.includeAdvertisingId = parsed
                                  return next
                                })
                              }
                              disabled={!canEdit || !isManagingBigQuery}
                            >
                              {bigQueryBooleanOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          {canEdit && (
                            <button
                              onClick={() => updateBigQueryLink(idx, () => null)}
                              style={{ alignSelf: "center" }}
                              disabled={!isManagingBigQuery}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {canEdit && (
                      <button
                        onClick={() =>
                          setSettings((prev) => ({
                            ...prev,
                            bigQueryLinks: [
                              ...(Array.isArray(prev.bigQueryLinks)
                                ? prev.bigQueryLinks
                                : []),
                              { project: "" },
                            ],
                          }))
                        }
                        disabled={!isManagingBigQuery}
                      >
                        Add BigQuery Link
                      </button>
                    )}
                  </>
                )}
              </div>
              <div style={{ display: currentStep === 8 ? "block" : "none" }}>
                <h3 style={{ marginTop: "1rem" }}>Channel Groupings</h3>
              {channelGroupings.length ? (
                channelGroupings.map((group, groupIdx) => {
                  const groupingRules = group.groupingRules || []
                  return (
                    <div
                      key={groupIdx}
                      style={{
                        border: "1px solid var(--border-color, #4b5563)",
                        padding: "0.75rem",
                        borderRadius: "8px",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.75rem",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.5rem",
                          }}
                        >
                          <div>
                            <label style={{ display: "block" }}>Display Name</label>
                            <input
                              type="text"
                              value={group.displayName || ""}
                              onChange={(e) =>
                                updateChannelGroupings((draft) => {
                                  draft.channelGroupings[groupIdx].displayName =
                                    e.target.value
                                })
                              }
                              disabled={!canEdit}
                              style={{ width: "100%" }}
                            />
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "0.5rem",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.25rem",
                                flex: "1 1 200px",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={Boolean(group.primary)}
                                onChange={(e) =>
                                  updateChannelGroupings((draft) => {
                                    const checked = e.target.checked
                                    draft.channelGroupings.forEach((cg, idx) => {
                                      if (idx === groupIdx) cg.primary = checked
                                      else if (checked) cg.primary = false
                                    })
                                  })
                                }
                                disabled={!canEdit}
                              />
                              Primary Channel Group
                            </label>
                            {canEdit && (
                              <button
                                type="button"
                                onClick={() =>
                                  updateChannelGroupings((draft) => {
                                    draft.channelGroupings.splice(groupIdx, 1)
                                  })
                                }
                                style={{ flex: "0 0 auto" }}
                              >
                                Remove Group
                              </button>
                            )}
                          </div>
                        </div>
                        <div>
                          <label style={{ display: "block" }}>Description</label>
                          <textarea
                            value={group.description || ""}
                            onChange={(e) =>
                              updateChannelGroupings((draft) => {
                                draft.channelGroupings[groupIdx].description =
                                  e.target.value
                              })
                            }
                            disabled={!canEdit}
                            rows={2}
                            style={{ width: "100%" }}
                          />
                        </div>
                        <div>
                          <h4 style={{ margin: "0.5rem 0" }}>Grouping Rules</h4>
                          {groupingRules.map((rule, ruleIdx) => {
                            const andGroups =
                              rule?.expression?.andGroup?.filterExpressions || []
                            return (
                              <div
                                key={ruleIdx}
                                style={{
                                  border:
                                    "1px solid var(--border-color, #6b7280)",
                                  borderRadius: "6px",
                                  padding: "0.75rem",
                                  marginBottom: "0.5rem",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "0.5rem",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <div
                                    style={{
                                      flex: "1 1 240px",
                                      minWidth: "220px",
                                    }}
                                  >
                                    <label style={{ display: "block" }}>
                                      Rule Display Name
                                    </label>
                                    <input
                                      type="text"
                                      value={rule.displayName || ""}
                                      onChange={(e) =>
                                        updateChannelGroupings((draft) => {
                                          draft.channelGroupings[groupIdx]
                                            .groupingRules[ruleIdx].displayName =
                                            e.target.value
                                        })
                                      }
                                      disabled={!canEdit}
                                      style={{ width: "100%" }}
                                    />
                                  </div>
                                  {canEdit && groupingRules.length > 1 && (
                                    <div style={{ flex: "0 0 auto" }}>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          updateChannelGroupings((draft) => {
                                            const rules =
                                              draft.channelGroupings[groupIdx]
                                                .groupingRules
                                            rules.splice(ruleIdx, 1)
                                            if (!rules.length) {
                                              rules.push(
                                                createDefaultGroupingRule()
                                              )
                                            }
                                          })
                                        }
                                      >
                                        Remove Rule
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <p style={{ margin: "0.5rem 0 0.25rem" }}>
                                  Traffic must match <strong>ALL</strong> of the
                                  following condition sets:
                                </p>
                                {andGroups.map((andExpr, andIdx) => {
                                  const orFilters =
                                    andExpr?.orGroup?.filterExpressions || []
                                  return (
                                    <div
                                      key={andIdx}
                                      style={{
                                        border:
                                          "1px dashed var(--border-color, #9ca3af)",
                                        borderRadius: "6px",
                                        padding: "0.75rem",
                                        marginTop: "0.5rem",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          gap: "0.5rem",
                                          marginBottom: "0.5rem",
                                        }}
                                      >
                                        <strong>
                                          Condition Set {andIdx + 1}
                                        </strong>
                                        {canEdit && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              updateChannelGroupings((draft) => {
                                                const andList =
                                                  draft.channelGroupings[groupIdx]
                                                    .groupingRules[ruleIdx]
                                                    .expression.andGroup
                                                    .filterExpressions
                                                andList.splice(andIdx, 1)
                                                if (!andList.length) {
                                                  andList.push(
                                                    createDefaultOrGroupExpression()
                                                  )
                                                }
                                              })
                                            }
                                          >
                                            Remove Condition Set
                                          </button>
                                        )}
                                      </div>
                                      <p style={{ margin: "0 0 0.5rem" }}>
                                        Matches <strong>ANY</strong> of the
                                        following filters:
                                      </p>
                                      {orFilters.map((filterExpr, filterIdx) => {
                                        const isNegated = Boolean(
                                          filterExpr?.notExpression
                                        )
                                        const filter =
                                          filterExpr?.notExpression?.filter ||
                                          filterExpr?.filter ||
                                          createDefaultChannelGroupFilter()
                                        const filterType = filter.inListFilter
                                          ? "list"
                                          : "string"
                                        const listValues =
                                          filter.inListFilter?.values || []
                                        const listText = listValues.join("\n")
                                        const matchType =
                                          filter.stringFilter?.matchType ||
                                          "EXACT"
                                        const value =
                                          filter.stringFilter?.value || ""
                                        const dimensionListId = `channel-group-dimension-${groupIdx}-${ruleIdx}-${andIdx}-${filterIdx}`
                                        const valueListId = `channel-group-value-${groupIdx}-${ruleIdx}-${andIdx}-${filterIdx}`
                                        const knownValueOptions = Array.from(
                                          new Set(
                                            getChannelGroupingValueSuggestions(
                                              filter.fieldName
                                            )
                                          )
                                        )
                                        const dimensionQuery =
                                          (filter.fieldName || "")
                                            .toLowerCase()
                                            .trim()
                                        const dimensionSuggestions =
                                          dimensionQuery
                                            ? CHANNEL_GROUP_DIMENSION_SUGGESTIONS.filter(
                                                (option) =>
                                                  option
                                                    .toLowerCase()
                                                    .includes(dimensionQuery)
                                              )
                                            : CHANNEL_GROUP_DIMENSION_SUGGESTIONS
                                        const suggestionPreview =
                                          knownValueOptions.slice(0, 12)
                                        return (
                                          <div
                                            key={filterIdx}
                                            style={{
                                              border:
                                                "1px solid var(--border-color, #9ca3af)",
                                              borderRadius: "6px",
                                              padding: "0.75rem",
                                              marginBottom: "0.5rem",
                                              boxSizing: "border-box",
                                              width: "100%",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: "0.5rem",
                                                alignItems: "flex-end",
                                              }}
                                            >
                                              <div style={{ flex: "0 1 160px" }}>
                                                <label style={{ display: "block" }}>
                                                  Action
                                                </label>
                                                <select
                                                  value={isNegated ? "exclude" : "include"}
                                                  onChange={(e) =>
                                                    updateFilterClause(
                                                      groupIdx,
                                                      ruleIdx,
                                                      andIdx,
                                                      filterIdx,
                                                      ({ setNegated }) =>
                                                        setNegated(
                                                          e.target.value ===
                                                            "exclude"
                                                        )
                                                    )
                                                  }
                                                  disabled={!canEdit}
                                                >
                                                  <option value="include">
                                                    Include
                                                  </option>
                                                  <option value="exclude">
                                                    Exclude
                                                  </option>
                                                </select>
                                              </div>
                                              <div style={{ flex: "0 1 180px" }}>
                                                <label style={{ display: "block" }}>
                                                  Filter Type
                                                </label>
                                                <select
                                                  value={filterType}
                                                  onChange={(e) =>
                                                    updateFilterClause(
                                                      groupIdx,
                                                      ruleIdx,
                                                      andIdx,
                                                      filterIdx,
                                                      ({ filter, setFilter }) => {
                                                        if (
                                                          e.target.value ===
                                                          "list"
                                                        ) {
                                                          const rawValues =
                                                            Array.isArray(
                                                              filter
                                                                .inListFilter?.values
                                                            )
                                                              ? filter
                                                                  .inListFilter
                                                                  ?.values
                                                              : filter
                                                                  .stringFilter
                                                                  ?.value
                                                              ? [
                                                                  filter
                                                                    .stringFilter
                                                                    ?.value,
                                                                ]
                                                              : []
                                                          const values = (rawValues || [])
                                                            .filter(
                                                              (v) =>
                                                                typeof v === "string" &&
                                                                v.length
                                                            )
                                                          setFilter({
                                                            fieldName:
                                                              filter.fieldName ||
                                                              "",
                                                            inListFilter: {
                                                              values,
                                                            },
                                                          })
                                                        } else {
                                                          const match =
                                                            filter.stringFilter &&
                                                            VALID_MATCH_TYPES.has(
                                                              filter
                                                                .stringFilter
                                                                ?.matchType || ""
                                                            )
                                                              ? filter
                                                                  .stringFilter
                                                                  ?.matchType
                                                              : "EXACT"
                                                          const val =
                                                            filter.stringFilter?.value ??
                                                            (Array.isArray(
                                                              filter
                                                                .inListFilter
                                                                ?.values
                                                            ) &&
                                                            filter
                                                              .inListFilter
                                                              ?.values.length
                                                              ? filter
                                                                  .inListFilter
                                                                  ?.values[0]
                                                              : "")
                                                          setFilter({
                                                            fieldName:
                                                              filter.fieldName ||
                                                              "",
                                                            stringFilter: {
                                                              matchType: match,
                                                              value: val,
                                                            },
                                                          })
                                                        }
                                                      }
                                                    )
                                                  }
                                                  disabled={!canEdit}
                                                >
                                                  <option value="string">
                                                    Single Value
                                                  </option>
                                                  <option value="list">
                                                    List of Values
                                                  </option>
                                                </select>
                                              </div>
                                              <div
                                                style={{
                                                  flex: "1 1 220px",
                                                  minWidth: "180px",
                                                  maxWidth: "100%",
                                                }}
                                              >
                                                <label style={{ display: "block" }}>
                                                  Dimension
                                                </label>
                                                <input
                                                  type="text"
                                                  value={filter.fieldName || ""}
                                                  placeholder="e.g. sessionSource"
                                                  list={dimensionListId}
                                                  onChange={(e) =>
                                                    updateFilterClause(
                                                      groupIdx,
                                                      ruleIdx,
                                                      andIdx,
                                                      filterIdx,
                                                      ({ filter }) => {
                                                        filter.fieldName =
                                                          e.target.value
                                                      }
                                                    )
                                                  }
                                                  disabled={!canEdit}
                                                  style={{ width: "100%" }}
                                                />
                                                <datalist id={dimensionListId}>
                                                  {dimensionSuggestions.map((option) => (
                                                    <option
                                                      key={option}
                                                      value={option}
                                                    />
                                                  ))}
                                                </datalist>
                                              </div>
                                              {canEdit && orFilters.length > 1 && (
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    updateChannelGroupings(
                                                      (draft) => {
                                                        const filters =
                                                          draft.channelGroupings[
                                                            groupIdx
                                                          ].groupingRules[
                                                            ruleIdx
                                                          ].expression.andGroup
                                                            .filterExpressions[
                                                              andIdx
                                                            ].orGroup
                                                            .filterExpressions
                                                        filters.splice(
                                                          filterIdx,
                                                          1
                                                        )
                                                        if (!filters.length) {
                                                          filters.push(
                                                            createDefaultFilterExpression()
                                                          )
                                                        }
                                                      }
                                                    )
                                                  }
                                                  style={{ alignSelf: "flex-start" }}
                                                >
                                                  Remove Filter
                                                </button>
                                              )}
                                            </div>
                                              {filterType === "string" ? (
                                                <>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      flexWrap: "wrap",
                                                      gap: "0.5rem",
                                                      marginTop: "0.5rem",
                                                    }}
                                                  >
                                                    <div>
                                                      <label style={{ display: "block" }}>
                                                        Match Type
                                                      </label>
                                                    <select
                                                      value={matchType}
                                                      onChange={(e) =>
                                                        updateFilterClause(
                                                          groupIdx,
                                                          ruleIdx,
                                                          andIdx,
                                                          filterIdx,
                                                          ({ filter, setFilter }) => {
                                                            if (filter.stringFilter) {
                                                              filter.stringFilter.matchType =
                                                                e.target.value
                                                            } else {
                                                              setFilter({
                                                                fieldName:
                                                                  filter.fieldName ||
                                                                  "",
                                                                stringFilter: {
                                                                  matchType:
                                                                    e.target.value,
                                                                  value: "",
                                                                },
                                                              })
                                                            }
                                                          }
                                                        )
                                                      }
                                                      disabled={!canEdit}
                                                    >
                                                      {MATCH_TYPE_OPTIONS.map((opt) => (
                                                        <option key={opt} value={opt}>
                                                          {opt}
                                                        </option>
                                                      ))}
                                                    </select>
                                                  </div>
                                                  <div
                                                    style={{
                                                      flex: "1 1 220px",
                                                      minWidth: "180px",
                                                      maxWidth: "100%",
                                                    }}
                                                  >
                                                    <label style={{ display: "block" }}>
                                                      Value
                                                    </label>
                                                    <input
                                                      type="text"
                                                      value={value}
                                                      list={valueListId}
                                                      onChange={(e) =>
                                                        updateFilterClause(
                                                          groupIdx,
                                                          ruleIdx,
                                                          andIdx,
                                                          filterIdx,
                                                          ({ filter, setFilter }) => {
                                                            if (filter.stringFilter) {
                                                              filter.stringFilter.value =
                                                                e.target.value
                                                            } else {
                                                              setFilter({
                                                                fieldName:
                                                                  filter.fieldName ||
                                                                  "",
                                                                stringFilter: {
                                                                  matchType: "EXACT",
                                                                  value: e.target.value,
                                                                },
                                                              })
                                                            }
                                                          }
                                                        )
                                                      }
                                                      disabled={!canEdit}
                                                      style={{ width: "100%" }}
                                                    />
                                                    {suggestionPreview.length > 0 && (
                                                      <datalist id={valueListId}>
                                                        {suggestionPreview.map((option) => (
                                                          <option
                                                            key={option}
                                                            value={option}
                                                          />
                                                        ))}
                                                      </datalist>
                                                    )}
                                                  </div>
                                                </div>
                                              </>
                                            ) : (
                                              <div style={{ marginTop: "0.5rem" }}>
                                                <label style={{ display: "block" }}>
                                                  Values (one per line)
                                                </label>
                                                <textarea
                                                  value={listText}
                                                  onChange={(e) =>
                                                    updateFilterClause(
                                                      groupIdx,
                                                      ruleIdx,
                                                      andIdx,
                                                      filterIdx,
                                                      ({ filter, setFilter }) => {
                                                        const values = e.target.value
                                                          .split(/\r?\n/)
                                                          .map((v) => v.trim())
                                                          .filter((v) => v.length)
                                                        if (filter.inListFilter) {
                                                          filter.inListFilter.values = values
                                                        } else {
                                                          setFilter({
                                                            fieldName:
                                                              filter.fieldName ||
                                                              "",
                                                            inListFilter: { values },
                                                          })
                                                        }
                                                      }
                                                    )
                                                  }
                                                  disabled={!canEdit}
                                                  rows={3}
                                                  style={{ width: "100%" }}
                                                />
                                              </div>
                                            )}
                                          </div>
                                        )
                                      })}
                                      {canEdit && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            updateChannelGroupings((draft) => {
                                              const filters =
                                                draft.channelGroupings[groupIdx]
                                                  .groupingRules[ruleIdx].expression
                                                  .andGroup.filterExpressions[andIdx]
                                                  .orGroup.filterExpressions
                                              filters.push(
                                                createDefaultFilterExpression()
                                              )
                                            })
                                          }
                                        >
                                          Add OR Filter
                                        </button>
                                      )}
                                    </div>
                                  )
                                })}
                                {canEdit && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateChannelGroupings((draft) => {
                                        const andList =
                                          draft.channelGroupings[groupIdx]
                                            .groupingRules[ruleIdx].expression
                                            .andGroup.filterExpressions
                                        andList.push(createDefaultOrGroupExpression())
                                      })
                                    }
                                  >
                                    Add Condition Set
                                  </button>
                                )}
                              </div>
                            )
                          })}
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() =>
                                updateChannelGroupings((draft) => {
                                  draft.channelGroupings[groupIdx].groupingRules.push(
                                    createDefaultGroupingRule()
                                  )
                                })
                              }
                            >
                              Add Grouping Rule
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              ) : (
                <p style={{ marginBottom: "0.5rem" }}>
                  No channel groups configured.
                </p>
              )}
              {canEdit && (
                <button
                  type="button"
                  onClick={() =>
                    updateChannelGroupings((draft) => {
                      draft.channelGroupings.push(createDefaultChannelGroup())
                    })
                  }
                >
                  Add Channel Group
                </button>
              )}
              </div>
              <div style={{ display: currentStep === 9 ? "block" : "none" }}>
                <h3 style={{ marginTop: "1rem" }}>Expected User Access</h3>
                <p style={{ marginBottom: "0.75rem" }}>
                  List the users or service accounts that should have access to this
                  property. During an audit we will compare the live user list
                  against this roster and highlight any unexpected changes.
                </p>
                {expectedUserAccess.length ? (
                  expectedUserAccess.map((user, idx) => {
                    const requiredRolesText = Array.isArray(user.requiredRoles)
                      ? user.requiredRoles.join(", ")
                      : ""
                    return (
                      <div
                        key={`expected-user-${idx}`}
                        style={{
                          border: "1px solid var(--border-color, #6b7280)",
                          padding: "0.75rem",
                          borderRadius: "8px",
                          marginBottom: "0.75rem",
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.75rem",
                        }}
                      >
                        <div className="ui-field-grid">
                          <Input
                            label="Email or Identifier"
                            value={user.identifier || ""}
                            onChange={(e) =>
                              updateExpectedUsers((draft) => {
                                draft.userAccess.expectedUsers[idx].identifier =
                                  e.target.value
                              })
                            }
                            placeholder="user@example.com"
                            disabled={!canEdit}
                          />
                          <Input
                            label="Display Name"
                            value={user.label || ""}
                            onChange={(e) =>
                              updateExpectedUsers((draft) => {
                                draft.userAccess.expectedUsers[idx].label =
                                  e.target.value
                              })
                            }
                            placeholder="Jane Admin"
                            disabled={!canEdit}
                          />
                          <Input
                            label="Required Roles"
                            value={requiredRolesText}
                            onChange={(e) => {
                              const nextRoles = e.target.value
                                .split(",")
                                .map((value) => value.trim())
                                .filter(Boolean)
                              updateExpectedUsers((draft) => {
                                draft.userAccess.expectedUsers[idx].requiredRoles =
                                  nextRoles
                              })
                            }}
                            placeholder="roles/editor, roles/analyst"
                            disabled={!canEdit}
                          />
                        </div>
                        <div>
                          <label className="ui-label" htmlFor={`expected-user-notes-${idx}`}>
                            Notes
                          </label>
                          <textarea
                            id={`expected-user-notes-${idx}`}
                            value={user.notes || ""}
                            onChange={(e) =>
                              updateExpectedUsers((draft) => {
                                draft.userAccess.expectedUsers[idx].notes =
                                  e.target.value
                              })
                            }
                            disabled={!canEdit}
                            rows={3}
                            style={{ width: "100%" }}
                            placeholder="Dealership owner, vendor contact, etc."
                          />
                        </div>
                        {canEdit && (
                          <div style={{ textAlign: "right" }}>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                updateExpectedUsers((draft) => {
                                  draft.userAccess.expectedUsers.splice(idx, 1)
                                })
                              }
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                      </div>
                    )
                  })
                ) : (
                  <p style={{ marginBottom: "0.5rem" }}>
                    No expected users have been configured.
                  </p>
                )}
                {canEdit && (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      updateExpectedUsers((draft) => {
                        draft.userAccess.expectedUsers.push({
                          identifier: "",
                          label: "",
                          requiredRoles: [],
                          notes: "",
                        })
                      })
                    }
                  >
                    Add Expected User
                  </Button>
                )}
              </div>
              {error && <p style={{ color: "red" }}>{error}</p>}
              <div
                style={{
                  marginTop: "1rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                <button onClick={handleCancel}>Cancel</button>
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                  }}
                >
                  {selectedId && !isCreating && canEdit && (
                    <Button variant="destructive" onClick={handleDelete}>
                      Delete
                    </Button>
                  )}
                  {currentStep > 0 && (
                    <button onClick={handlePreviousStep}>Back</button>
                  )}
                  {!isFinalStep && (
                    <button
                      onClick={handleNextStep}
                      disabled={!canProceedToNext}
                    >
                      Next
                    </button>
                  )}
                  {(isFinalStep || (selectedId && !isCreating)) && (
                    <button
                      onClick={handleSave}
                      disabled={!canEdit || !name.trim()}
                    >
                      {selectedId && !isCreating ? "Save & Close" : "Save"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
